import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  getDoc,
  getDocs,
  writeBatch,
  arrayUnion,
  FieldPath,
  runTransaction,
  increment,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';
import {
  cleanupConversationAttachments,
  getAIResponseStream,
  getLatestChatArtifactId,
  initChatSession,
} from '../api/chatApi';
import { collectConversationAttachmentRefs } from '../utils/conversationAttachmentCleanup';
import { turnMessageDocIds } from '../api/chatTurnRecovery';
import {
  normalizeChatModelId,
  toPersistedChatSelection,
  toChatApiModelId,
} from '../constants/aiModels';
import { incrementAnonymousMessageCount } from '../utils/anonymousUser';
import { createLatestFrameBatcher } from '../utils/frameBatcher';
import { getApiBaseUrl } from '../utils/apiConfig';
import { isRateLimitError, getRateLimitMessage } from '../utils/rateLimitHandler';
import {
  normalizeConversationTitle,
  sortConversationsPinnedFirst,
} from '../utils/conversationList';
import {
  removeFailedTurn,
  removeOptimisticTurn,
} from '../utils/chatMessageState';
import { mergeWebImageStrip, finalizeWebImages } from '../utils/webImageStrips';
import { mergeRichBlock, finalizeRichBlocks } from '../utils/richBlocks';
import {
  CANVAS_FORMAT_VERSION,
  SUPPORTED_CANVAS_FORMAT_VERSIONS,
} from '../utils/canvasSchema';
import { isChatKeepPartialEnabled, stripChatSystemTags } from '../utils/chatPartialAnswer';
import { attachmentMetaToMessageFields } from '../utils/chatAttachmentMeta';
import {
  applyArtifactChunk,
  detectPendingArtifactFromContent,
  isArtifactProgressChunk,
} from '../utils/chatArtifactProgress';
import { upsertArtifactRecord } from '../utils/artifactRecords';
import { normalizeLiveAnchor } from '../utils/liveArtifactAnchor';
import { syncLibraryTextOutline } from '../utils/syncLibraryTextOutline';
import { isAnonymousConversationId } from '../utils/anonymousConversationId';
import { buildAnonymousChatHistory } from '../utils/anonymousChatHistory';
import {
  restoreReplacedTurn,
  swapReplacedTurn,
  turnReplaceFailedNotice,
  withoutReplacedTurn,
} from '../utils/chatTurnReplace';
import { CHAT_HANDOFF_SUGGESTION_CATEGORIES } from '../utils/chatHandoff';
import {
  isPersistableLibraryFileId,
  normalizeDismissedLibrarySuggestionFileIds,
} from '../utils/librarySuggestion';
import { trackChatUxEvent } from '../utils/eventTracker';
import { getTrackingErrorCode } from '../utils/trackingErrorCode';
import { linkSpeechKey } from '../utils/messageSpeechPlayer';
import { appendActivitySteps } from '../utils/activitySteps';

// Firestore 문서 한도(1MB) 아래에서 캔버스 JSON을 담기 위한 안전선.
// 캔버스에 data URL 이미지가 들어가면 쉽게 넘는다 → 넘으면 서버 저장을 건너뛰고 호출부가 로컬 미러에만 둔다.
const WORKSHEET_CANVAS_MAX_BYTES = 900_000;
const IMAGE_CONVERSATION_CREATE_WAIT_MS = 8000;
const ARTIFACT_RECORDS_LIMIT = 20;
const CHAT_SAVE_FAILED_NOTICE = '대화 기록을 저장하지 못했어요. 필요한 답변은 지금 복사해 두세요.';

// 로컬 스토리지 키 상수
const ANONYMOUS_CONVERSATIONS_KEY = 'whatquiz_anonymous_conversations';
const ANONYMOUS_CURRENT_CONVERSATION_KEY = 'whatquiz_anonymous_current_conversation';
const ANONYMOUS_MESSAGES_PREFIX = 'whatquiz_anonymous_messages_';

// addDoc 은 '없을 때만 만든다' 조건이라 첫 쓰기 확인을 못 받은 SDK 재시도가 already-exists 로 실패한다.
// id 를 먼저 정해 setDoc 으로 쓰면 재시도는 같은 문서 덮어쓰기다 (conversations·messages 규칙 모두 소유자 update 허용).
const _setDocWithRetry = async (ref, data, retries = 0) => {
  try {
    await setDoc(ref, data);
  } catch (error) {
    if (retries <= 0) throw error;
    await new Promise((resolve) => setTimeout(resolve, 600));
    await _setDocWithRetry(ref, data, retries - 1);
  }
  return ref;
};

const _createMessageDoc = async (conversationId, data, { retries = 0, id = null } = {}) => {
  const messages = collection(db, 'conversations', conversationId, 'messages');
  return _setDocWithRetry(id ? doc(messages, id) : doc(messages), data, retries);
};

// 바꿔 보낸 턴: 새 사용자·답변 문서를 쓰고 옛 두 문서를 지우는 일을 한 배치로 한다(목록에 두 벌이 보이는 순간이 없다).
const _commitReplacedTurnDocs = async (conversationId, { user, assistant, deleteIds = [] }, retries = 1) => {
  const messages = collection(db, 'conversations', conversationId, 'messages');
  const userRef = doc(messages, user.id || doc(messages).id);
  const aiRef = doc(messages, assistant.id || doc(messages).id);
  // 한 배치의 serverTimestamp 는 두 문서가 같은 시각이 되고, 같은 시각은 문서 id 순(turn_x_ai < turn_x_user)이라
  // 답변이 질문 위로 간다. 새 질문은 옛 질문의 서버 시각을 물려받아 같은 자리에 둔다.
  let userData = user.data;
  const oldUserId = deleteIds.find((id) => id && id.endsWith('_user'));
  if (oldUserId) {
    try {
      const oldUser = await getDoc(doc(messages, oldUserId));
      const oldTimestamp = oldUser.exists() ? oldUser.data()?.timestamp : null;
      if (oldTimestamp) userData = { ...user.data, timestamp: oldTimestamp };
    } catch (_) {
      // 읽지 못하면 서버 시각 그대로 쓴다.
    }
  }
  const commit = async (left) => {
    const batch = writeBatch(db);
    batch.set(userRef, userData);
    batch.set(aiRef, assistant.data);
    deleteIds
      .filter((id) => id && id !== userRef.id && id !== aiRef.id)
      .forEach((id) => batch.delete(doc(messages, id)));
    try {
      await batch.commit();
    } catch (error) {
      if (left <= 0) throw error;
      await new Promise((resolve) => setTimeout(resolve, 600));
      await commit(left - 1);
    }
  };
  await commit(retries);
  return aiRef;
};

const _createConversationDoc = async (data, { retries = 0, ref = null } = {}) => (
  _setDocWithRetry(ref || doc(collection(db, 'conversations')), data, retries)
);

const _errorToken = (value, fallback) => (
  String(value || '').trim().slice(0, 60).replace(/[^a-zA-Z0-9._-]/g, '_') || fallback
);

// 오류로 끝난 턴은 메시지로 저장하지 않는다('다시 보내기'가 화면에서 지우고 같은 글을 새로 보낸다).
// 대신 대화 문서에 코드·모델·카테고리만 남겨 어느 대화에서 실패했는지 셀 수 있게 한다. 글 내용은 남기지 않는다.
export const recordTurnError = (conversationId, { code, retryable, model, category } = {}) => {
  if (!conversationId || isAnonymousConversationId(conversationId)) return Promise.resolve(false);
  return Promise.resolve()
    .then(() => updateDoc(doc(db, 'conversations', conversationId), {
      lastTurnError: {
        code: _errorToken(code, 'unknown'),
        retryable: retryable === true,
        model: _errorToken(model, 'unknown'),
        category: _errorToken(category, 'general'),
        at: serverTimestamp(),
      },
      turnErrorCount: increment(1),
    }))
    .then(() => true, () => false);
};

const _withSaveStage = (error, stage) => {
  if (error && typeof error === 'object' && !error.saveStage) {
    try {
      error.saveStage = stage;
    } catch (_) {
      // 고정된 오류 객체면 단계 표시 없이 그대로 던진다.
    }
  }
  return error;
};

// 🧠 AI 제안 카드(memory_proposal 등) 정규화.
// 백엔드 complete 청크의 suggestions[] 는 그대로 메시지 문서에 저장하고,
// 교사의 응답은 suggestion_results: { [suggestionId]: { result, memory_id?, resolved_at } } 로 기록한다.
const _normalizeSuggestions = (value) => (
  Array.isArray(value)
    ? value.filter((item) => item && typeof item === 'object' && item.id)
    : []
);
const _normalizeSuggestionResults = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);
// 🧠 투명화(K-1): 이 답변에 실제로 반영된 선생님 정보 필드 이름·기억 개수.
// 값(과목명 등)은 오지 않는다. 비어 있으면 null 로 정규화해 배지를 숨긴다.
const _normalizePersonalizationUsed = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const profileFields = Array.isArray(value.profile_fields)
    ? value.profile_fields.filter((item) => typeof item === 'string' && item)
    : [];
  const memoryCount = Number(value.memory_count) > 0 ? Math.floor(Number(value.memory_count)) : 0;
  if (profileFields.length === 0 && memoryCount === 0) return null;
  return { profile_fields: profileFields, memory_count: memoryCount };
};
// 📁 이 답변이 읽은 내 자료함 자료 [{id, title}] (최대 5개)
const _normalizeLibraryReferences = (value) => (
  Array.isArray(value)
    ? value
      .filter((item) => item && typeof item.id === 'string' && item.id && typeof item.title === 'string')
      .slice(0, 5)
      .map((item) => ({ id: item.id, title: item.title.slice(0, 60) }))
    : []
);
// 🗂️ 이 답변이 읽은 지난 대화 [{id, title, date(YYYY-MM-DD)}] (최대 5개, 제목은 학생 이름을 가린 것)
const _normalizePastChatReferences = (value) => (
  Array.isArray(value)
    ? value
      .filter((item) => item && typeof item.id === 'string' && item.id && typeof item.title === 'string')
      .slice(0, 5)
      .map((item) => ({
        id: item.id,
        title: item.title.slice(0, 60),
        date: /^\d{4}-\d{2}-\d{2}$/.test(String(item.date || '')) ? item.date : '',
      }))
    : []
);
// Firestore 점 표기 필드 경로에서 금지된 문자(~ * / [ ]). 제안 id는 UUID 이지만 방어적으로 검사한다.
const FIRESTORE_FIELD_PATH_RESERVED = /[~*/[\]]/;
const HANDOFF_DISMISS_TARGET_SET = new Set(CHAT_HANDOFF_SUGGESTION_CATEGORIES);
const _normalizeDismissedHandoffTargets = (value) => (
  Array.isArray(value)
    ? value.filter((item) => HANDOFF_DISMISS_TARGET_SET.has(item))
    : []
);

// 카테고리명 매핑 (ChatPage.js의 CATEGORY_CONFIGS와 동기화 필요)
// 레거시 student_counseling/teacher_care 는 통합된 counseling_care 의 표시명을 그대로 사용해
// 기존 Firestore 대화의 lastCategoryName 이 자연스럽게 새 라벨로 표시되도록 한다.
// ※ /chat 헤더("할 일 바꾸기")는 이 문자열을 그대로 쓰지 않는다. lastCategory(ID) 기준으로
//    CATEGORY_CONFIGS 라벨을 쓰고, ID 로 라벨을 찾을 수 없는 레거시 대화에서만 앞 이모지를 떼어
//    fallback 으로 쓴다 (utils/chatCategoryLabel.js). 이미 저장된 옛 이모지 라벨은 그 유틸이 처리한다.
const CATEGORY_NAME_MAP = {
  'record': '생기부 작성',
  'document': 'AI 한글(HWP) 작성',
  'worksheet_gen': '학습지 생성',
  'parent_communication': '학부모 소통',
  'counseling_care': '상담·마음',
  'admin_docs': '행정 업무',
  'student_counseling': '상담·마음',
  'teacher_care': '상담·마음',
  'ppt_maker': 'PPT 만들기',
  'general': '자유 대화',
  'pro_ai': '자유'
};

const _deserializeOutline = (raw) => {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); }
    catch (e) { console.warn('outline 역직렬화 실패:', e); return null; }
  }
  return raw;
};

const _toConversationListItem = (conversationDoc) => {
  const data = conversationDoc.data();

  return {
    id: conversationDoc.id,
    title: data.title || '제목 없음',
    titleManuallyEdited: data.titleManuallyEdited === true,
    date: data.updatedAt ? new Date(data.updatedAt.toDate()).toLocaleDateString('ko-KR', {
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    }) : '날짜 없음',
    sessionId: data.sessionId,
    lastCategory: data.lastCategory || 'general',
    lastCategoryName: data.lastCategoryName || '자유 대화',
    selectedModel: data.selectedModel || 'gpt-5.4-nano',
    // 이미지 채팅: 사용자가 직접 고른 모델만 복원. 플래그 없는 옛 파생값은 자동으로 본다.
    selectedImageModel: typeof data.selectedImageModel === 'string' && data.selectedImageModel
      ? data.selectedImageModel
      : null,
    imageModelExplicit: data.imageModelExplicit === true,
    // P3: 대화 안 인라인 이미지 생성 세션 (이미지 채팅 sessionId·lastCategory 와 분리)
    inlineImageSessionId: typeof data.inlineImageSessionId === 'string' ? data.inlineImageSessionId : null,
    pinned: data.pinned === true,
    pinnedAt: data.pinnedAt ? data.pinnedAt.toDate?.() || data.pinnedAt : null,
    updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
    pptDownloadUrl: data.pptDownloadUrl || null,
    pptFilename: data.pptFilename || null,
    pptOutline: _deserializeOutline(data.pptOutline),
    pptPreviewUrls: data.pptPreviewUrls || null,
    pptOriginalUrls: data.pptOriginalUrls || null,
    pptDownloadMode: data.pptDownloadMode || null,
    pptActiveVersionId: data.pptActiveVersionId || null,
    // 라이브 결과물을 둘 메시지 (utils/liveArtifactAnchor)
    pptLiveAnchor: normalizeLiveAnchor(data.pptLiveAnchor),
    worksheetLiveAnchor: normalizeLiveAnchor(data.worksheetLiveAnchor),
    worksheetOutline: _deserializeOutline(data.worksheetOutline),
    worksheetDownloadUrl: data.worksheetDownloadUrl || null,
    worksheetFilename: data.worksheetFilename || null,
    worksheetPreviewUrls: data.worksheetPreviewUrls || null,
    worksheetOriginalUrls: data.worksheetOriginalUrls || null,
    worksheetActiveVersionId: data.worksheetActiveVersionId || null,
    // 편집 상태 영속화: 사용자가 자유 캔버스에서 고친 학습지 그대로(재빌드 없이) 복원
    worksheetCanvas: _deserializeOutline(data.worksheetCanvas),
    worksheetCanvasVersionId: data.worksheetCanvasVersionId || null,
    worksheetCanvasUpdatedAt: data.worksheetCanvasUpdatedAt || null,
    // 산출물 레코드 (utils/artifactRecords 계약) — 최신 우선, 최대 20개
    worksheetArtifacts: Array.isArray(data.worksheetArtifacts) ? data.worksheetArtifacts : [],
    pptArtifacts: Array.isArray(data.pptArtifacts) ? data.pptArtifacts : [],
    dismissedHandoffTargets: _normalizeDismissedHandoffTargets(
      data.dismissedHandoffTargets
    ),
    dismissedLibrarySuggestionFileIds: normalizeDismissedLibrarySuggestionFileIds(
      data.dismissedLibrarySuggestionFileIds
    ),
  };
};

// 바뀌지 않은 대화는 이전 항목 객체를 그대로 돌려준다 (사이드바 항목 memo 가 이 참조 동일성에 기댄다).
const _mapConversationSnapshot = (snapshot, itemCache) => {
  const changes = typeof snapshot.docChanges === 'function' ? snapshot.docChanges() : null;
  if (changes) {
    changes.forEach((change) => {
      if (change.type === 'removed') {
        itemCache.delete(change.doc.id);
      } else {
        itemCache.set(change.doc.id, _toConversationListItem(change.doc));
      }
    });
  } else {
    itemCache.clear();
  }
  return snapshot.docs.map((conversationDoc) => {
    const cached = itemCache.get(conversationDoc.id);
    if (cached) return cached;
    const item = _toConversationListItem(conversationDoc);
    itemCache.set(conversationDoc.id, item);
    return item;
  });
};

// 챗 컨텍스트 생성
const ChatContext = createContext();

// 챗 컨텍스트 프로바이더 컴포넌트
export const ChatProvider = ({ children }) => {
  const { currentUser, isAnonymous, userDetails } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversationState] = useState(null);
  // 화면이 지금 고른 대화 — await 뒤에 「그사이 사용자가 다른 대화를 골랐는지」를 보려고 setter 와 함께 즉시 갱신한다.
  const currentConversationRef = useRef(null);
  const setCurrentConversation = useCallback((conversationId) => {
    currentConversationRef.current = conversationId;
    setCurrentConversationState(conversationId);
    if (!isAnonymous) return;
    if (conversationId) {
      localStorage.setItem(
        ANONYMOUS_CURRENT_CONVERSATION_KEY,
        conversationId
      );
    } else {
      localStorage.removeItem(ANONYMOUS_CURRENT_CONVERSATION_KEY);
    }
  }, [isAnonymous]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 세션 ID 관리를 위한 상태 추가
  const [sessionMap, setSessionMap] = useState({});
  
  // API 호출 취소를 위한 AbortController 추가
  const [abortController, setAbortController] = useState(null);
  
  // 비로그인 → 로그인으로 바뀌면 로컬 익명 대화 선택·세션 매핑을 정리한다 (로그인 후 그 id 로 저장하면 실패).
  const wasAnonymousRef = useRef(isAnonymous);
  useEffect(() => {
    const wasAnonymous = wasAnonymousRef.current;
    wasAnonymousRef.current = isAnonymous;
    if (!wasAnonymous || isAnonymous) return;
    if (isAnonymousConversationId(currentConversationRef.current)) currentConversationRef.current = null;
    setCurrentConversationState((previous) => (
      isAnonymousConversationId(previous) ? null : previous
    ));
    setSessionMap((previousMap) => {
      const anonymousIds = Object.keys(previousMap).filter(isAnonymousConversationId);
      if (anonymousIds.length === 0) return previousMap;
      const nextMap = { ...previousMap };
      anonymousIds.forEach((conversationId) => {
        delete nextMap[conversationId];
      });
      return nextMap;
    });
    localStorage.removeItem(ANONYMOUS_CURRENT_CONVERSATION_KEY);
  }, [isAnonymous]);

  // 🚀 스트리밍 상태 관리 (Firebase onSnapshot 충돌 방지)
  const [isStreaming, setIsStreaming] = useState(false);

  // 로그아웃·다른 계정 로그인은 새로고침 없이 일어난다. 앞 계정의 대화를 내리지 않으면 공용 PC에서
  // 다음 사람이 그 대화를 보고 이어 보내고, 서버는 남의 대화라 맥락 없이 답하며 메시지는 어디에도 저장되지 않는다.
  const accountUidRef = useRef(currentUser?.uid || null);
  const currentUid = currentUser?.uid || null;
  // 주소(?id=)가 이 대화를 계속 가리키면 화면이 다시 열려 하므로, 화면이 주소를 비울 수 있게 알린다.
  const [unreadableConversationId, setUnreadableConversationId] = useState(null);
  useEffect(() => {
    const previousUid = accountUidRef.current;
    accountUidRef.current = currentUid;
    if (!previousUid || previousUid === currentUid) return;
    // 앞 계정의 답이 생성 중이면 끊는다. 생성 중에는 메시지 구독을 붙이지 않아 다음 사람이 자기 대화를 못 연다.
    abortController?.abort();
    setAbortController(null);
    setIsStreaming(false);
    setLoading(false);
    setCurrentConversation(null);
    setMessages([]);
    setSessionMap({});
    setUnreadableConversationId(null);
  }, [currentUid, setCurrentConversation, abortController]);
  
  // 🆕 선택된 모델 상태 관리 (WhatQuiz-Core 1.0 기본 - 무료 사용자)
  const [selectedModel, setSelectedModel] = useState('gpt-5.4-nano');
  
  // 오박사 대화 식별 함수
  const isOhDoctorConversation = (conversation) => {
    return conversation.title.startsWith('[오박사 상담실]');
  };
  
  // 기존 오박사 대화 찾기 (전체 대화에서 검색)
  const findExistingOhDoctorConversation = useCallback(async () => {
    // 익명 사용자인 경우
    if (isAnonymous && userDetails) {
      const storedConversations = localStorage.getItem(ANONYMOUS_CONVERSATIONS_KEY);
      if (storedConversations) {
        try {
          const allConversations = JSON.parse(storedConversations);
          return allConversations.find(conv => isOhDoctorConversation(conv));
        } catch (err) {
          console.error('오박사 대화 찾기 오류:', err);
          return null;
        }
      }
      return null;
    }
    
    // Firebase 사용자인 경우
    if (currentUser) {
      try {
        const q = query(
          collection(db, 'conversations'),
          where('userId', '==', currentUser.uid),
          where('title', '>=', '[오박사 상담실]'),
          where('title', '<', '[오박사 상담실]\uf8ff')
        );
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          return {
            id: doc.id,
            title: doc.data().title,
            date: doc.data().updatedAt ? new Date(doc.data().updatedAt.toDate()).toLocaleDateString('ko-KR', {
              year: 'numeric', 
              month: 'long', 
              day: 'numeric'
            }) : '날짜 없음',
            sessionId: doc.data().sessionId
          };
        }
      } catch (err) {
        console.error('오박사 대화 찾기 오류:', err);
      }
    }

    return null;
  }, [isAnonymous, userDetails, currentUser]);

  // 익명 사용자의 대화 데이터 로드
  const loadAnonymousConversations = useCallback(() => {
    const storedConversations = localStorage.getItem(ANONYMOUS_CONVERSATIONS_KEY);
    if (storedConversations) {
      try {
        const allConversations = JSON.parse(storedConversations).map((conv) => ({
          ...conv,
          dismissedHandoffTargets: _normalizeDismissedHandoffTargets(
            conv.dismissedHandoffTargets
          ),
          dismissedLibrarySuggestionFileIds: normalizeDismissedLibrarySuggestionFileIds(
            conv.dismissedLibrarySuggestionFileIds
          ),
        }));
        
        // 🩺 오박사 대화 필터링: 일반 대화 목록에서 제외
        const regularConversations = allConversations.filter(conv => 
          !conv.title.startsWith('[오박사 상담실]')
        );
        
        setConversations(sortConversationsPinnedFirst(regularConversations));
        
        // 세션 매핑 정보 업데이트 (모든 대화 포함)
        const newSessionMap = {};
        allConversations.forEach(conv => {
          if (conv.sessionId) {
            newSessionMap[conv.id] = conv.sessionId;
          }
        });
        setSessionMap(prevMap => ({ ...prevMap, ...newSessionMap }));
      } catch (err) {
        console.error('익명 대화 데이터 로드 오류:', err);
        setConversations([]);
      }
    } else {
      setConversations([]);
    }
    
      // 현재 대화 ID 불러오기
  const currentConvId = localStorage.getItem(ANONYMOUS_CURRENT_CONVERSATION_KEY);
  if (currentConvId) {
    setCurrentConversation(currentConvId);
  }
  
  // 🩺 오박사 상담실 페이지에서 새로고침 시 자동으로 오박사 대화 선택
  if (window.location.pathname === '/counseling') {
    setTimeout(async () => {
      try {
        const existingOhDoctorConversation = await findExistingOhDoctorConversation();
        if (existingOhDoctorConversation) {
          //console.log('🔄 오박사 대화 자동 선택:', existingOhDoctorConversation.id);
          setCurrentConversation(existingOhDoctorConversation.id);
        }
      } catch (err) {
        console.error('오박사 대화 자동 선택 오류:', err);
      }
    }, 500); // 0.5초 후 실행
  }
  }, [findExistingOhDoctorConversation, setCurrentConversation]);
  
  // 익명 사용자의 현재 대화 메시지 로드
  const loadAnonymousMessages = useCallback((conversationId) => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    
    let nextMessages = [];
    const storedMessages = localStorage.getItem(`${ANONYMOUS_MESSAGES_PREFIX}${conversationId}`);
    if (storedMessages) {
      try {
        nextMessages = JSON.parse(storedMessages);
      } catch (err) {
        console.error('익명 메시지 데이터 로드 오류:', err);
      }
    }
    // 저장 못 한 턴(saveFailedIn)은 저장소에 없으므로 대화를 옮기기 전까지 화면 목록을 유지한다.
    setMessages(prev => (prev.some(msg => msg.saveFailedIn) ? prev : nextMessages));
  }, []);
  
  // 익명 사용자의 대화 데이터 저장
  const saveAnonymousConversations = (newConversations) => {
    localStorage.setItem(ANONYMOUS_CONVERSATIONS_KEY, JSON.stringify(newConversations));
  };
  

  
  // 익명 사용자의 메시지 데이터 저장
  const saveAnonymousMessages = (conversationId, newMessages) => {
    localStorage.setItem(`${ANONYMOUS_MESSAGES_PREFIX}${conversationId}`, JSON.stringify(newMessages));
  };
  
  // 🎯 [재통합] useEffect를 다시 하나로 통합합니다.
  // "선 생성" 원칙 덕분에 더 이상 복잡한 분기가 필요 없습니다.
  useEffect(() => {
    if (isAnonymous && userDetails) {
      loadAnonymousConversations();
      return;
    }
    if (!currentUser) {
      setConversations([]);
      return;
    }
    const q = query(
      collection(db, 'conversations'),
      where('userId', '==', currentUser.uid),
      orderBy('updatedAt', 'desc')
    );
    const conversationItemCache = new Map();
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allConversations = _mapConversationSnapshot(snapshot, conversationItemCache);
      
      // 🩺 오박사 대화 필터링: 일반 대화 목록에서 제외
      //console.log('🔍 전체 대화 목록 (Firebase):', allConversations.map(conv => ({ id: conv.id, title: conv.title })));
      
      const regularConversations = sortConversationsPinnedFirst(allConversations.filter(conv => 
        !conv.title.startsWith('[오박사 상담실]')
      ));
      
     // console.log('🔍 필터링된 일반 대화 목록:', regularConversations.map(conv => ({ id: conv.id, title: conv.title })));
      
      // 세션 매핑 정보 구성 (모든 대화 포함)
      const newSessionMap = {};
      allConversations.forEach(conv => {
        if (conv.sessionId) {
          newSessionMap[conv.id] = conv.sessionId;
        }
      });
      setSessionMap(prevMap => ({ ...prevMap, ...newSessionMap }));
      
      // 일반 대화 목록만 설정 (오박사 대화 제외)
      setConversations(regularConversations);
      setError(null);
      
      // 🩺 오박사 상담실 페이지에서 새로고침 시 자동으로 오박사 대화 선택
      if (window.location.pathname === '/counseling') {
        setTimeout(async () => {
          try {
            const existingOhDoctorConversation = await findExistingOhDoctorConversation();
            if (existingOhDoctorConversation) {
              //console.log('🔄 오박사 대화 자동 선택 (Firebase):', existingOhDoctorConversation.id);
              setCurrentConversation(existingOhDoctorConversation.id);
            }
          } catch (err) {
            console.error('오박사 대화 자동 선택 오류 (Firebase):', err);
          }
        }, 500); // 0.5초 후 실행
      }
    }, (err) => {
      setError('대화 목록을 불러오는 중 오류가 발생했습니다.');
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [
    currentUser,
    isAnonymous,
    userDetails,
    findExistingOhDoctorConversation,
    loadAnonymousConversations,
    setCurrentConversation,
  ]);

  // 대화 전환 시 에러 메시지 정리
  const prevConversationRef = useRef(currentConversation);
  // 저장 실패 안내를 띄운 대화 — 익명 새 대화는 답을 받은 뒤 임시 ID 에서 실제 ID 로 바뀌는데, 이건 전환이 아니다.
  const saveFailedNoticeConversationRef = useRef(null);
  useEffect(() => {
    if (prevConversationRef.current !== currentConversation) {
      prevConversationRef.current = currentConversation;
      setMessages(prev => prev.filter(msg => (
        !msg.error && (!msg.saveFailedIn || msg.saveFailedIn === currentConversation)
      )));
      if (saveFailedNoticeConversationRef.current !== currentConversation) {
        saveFailedNoticeConversationRef.current = null;
        setError(null);
      }
    }
  }, [currentConversation]);

  // Effect 2: "현재 선택된 대화의 메시지" 로딩을 전담합니다.
  useEffect(() => {
    if (isStreaming) {
      return;
    }

    if (isAnonymous && userDetails) {
      loadAnonymousMessages(currentConversation);
      return;
    }
    if (!currentUser || !currentConversation) {
      setMessages([]);
      return;
    }
    const subscribedConversation = currentConversation;
    const q = query(
      collection(db, 'conversations', currentConversation, 'messages'),
      orderBy('timestamp', 'asc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messageList = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          // 어느 대화의 메시지인지 — 대화 전환 직후 이전 대화 messages 가 잠깐 남는 구간에서
          // 대화별 파생 상태(예: 미리보기 복원)가 잘못된 대화를 보지 않도록 명시한다.
          conversationId: currentConversation,
          content: data.content,
          role: data.role,
          timestamp: data.timestamp ? data.timestamp.toDate() : new Date(),
          category: data.category || undefined,
          streaming: false,
          isSearching: false,
          web_search_queries: data.web_search_queries || [],
          web_search_sources: data.web_search_sources || [],
          reasoning_steps: data.reasoning_steps || [],
          response_id: data.response_id || undefined,
          response_chain_len: data.response_chain_len || 0,
          modelId: data.modelId || data.effective_model || undefined,
          effective_model: data.effective_model || data.modelId || undefined,
          requestedModelId: data.requestedModelId || undefined,
          reasoningEffort: data.reasoningEffort || undefined,
          memory_update: data.memory_update || null,
          // 🧠 AI 제안 카드와 교사의 응답 결과 — 메시지 문서에 저장되어 새로고침/멀티워커에도 유지된다.
          suggestions: _normalizeSuggestions(data.suggestions),
          suggestion_results: _normalizeSuggestionResults(data.suggestion_results),
          personalization_used: _normalizePersonalizationUsed(data.personalization_used),
          library_references: _normalizeLibraryReferences(data.library_references),
          past_chat_references: _normalizePastChatReferences(data.past_chat_references),
          artifacts: Array.isArray(data.artifacts) ? data.artifacts : [],
          promise_without_file: data.promise_without_file === true,
          edit_not_applied: data.edit_not_applied === true,
          edit_not_applied_target: data.edit_not_applied_target === 'artifact' ? 'artifact' : undefined,
          action_not_taken: data.action_not_taken === true,
          // 🃏 되묻기 카드(첫 턴)와 교사의 응답 — 메시지 문서가 진실(새로고침·멀티워커 유지)
          clarify_card: data.clarify_card && typeof data.clarify_card === 'object' ? data.clarify_card : null,
          clarify_result: data.clarify_result && typeof data.clarify_result === 'object' ? data.clarify_result : null,
          clarify_assumptions: Array.isArray(data.clarify_assumptions) ? data.clarify_assumptions : [],
          // 🧩 P5 자동 스킬 라벨 {skill,label,text,source}
          applied_skill: data.applied_skill && typeof data.applied_skill === 'object' && data.applied_skill.skill ? data.applied_skill : null,
          // 🖼️ 본문 [[image_strip:N]] 자리에 들어가는 웹 이미지 묶음 — 메시지 문서에 저장되어 새로고침에도 유지
          web_images: Array.isArray(data.web_images) ? data.web_images : [],
          // 🃏 본문 [[rich:N]] 자리의 사진 추천 카드 묶음
          rich_blocks: Array.isArray(data.rich_blocks) ? data.rich_blocks : [],
          followup_form: data.followup_form && typeof data.followup_form === 'object' ? data.followup_form : null,
          followup_form_result: data.followup_form_result && typeof data.followup_form_result === 'object' ? data.followup_form_result : null,
          next_chips: Array.isArray(data.next_chips) ? data.next_chips : [],
          hasImage: data.hasImage || false,
          imageUrl: data.imageUrl || null,
          inline_image: data.inline_image && typeof data.inline_image === 'object' ? data.inline_image : null,
          // 이미지 채팅 메타: 이 턴에 실제 쓰인 모델 id (selectedImageModel 우선, 구버전 effective_image_model 호환)
          selectedImageModel: data.selectedImageModel || data.effective_image_model || undefined,
          // 이미지 디렉터가 실제로 그린 지시문 — 새로고침 뒤 다음 턴 대화 맥락(conversationTurns)에 쓰인다.
          effectivePrompt: typeof data.effectivePrompt === 'string' ? data.effectivePrompt : undefined,
          // 이미지 메뉴에서 코드로 정확히 그린 그림의 종류(music) — 다음 턴 디렉터가 본다.
          exactFigure: typeof data.exactFigure === 'string' ? data.exactFigure : undefined,
          // 이 사용자 메시지에서 새로 붙인 파일 — 다시 만들기·고쳐 보내기가 같은 '새 첨부' 표시로 다시 보낸다.
          ...(Array.isArray(data.newlyAttachedFileIds) && data.newlyAttachedFileIds.length > 0
            ? { newlyAttachedFileIds: data.newlyAttachedFileIds }
            : {}),
          // 공유 화면이 '선생님이 올린 파일' 이름을 보여 줄 때 쓴다(파일 자체는 공유하지 않는다).
          ...(Array.isArray(data.attachedFileIds) && Array.isArray(data.attachedFileNames)
            ? { attachedFileIds: data.attachedFileIds, attachedFileNames: data.attachedFileNames }
            : {}),
          // 멈추거나 끊겨 받은 만큼만 남은 답 — 답 아래 한 줄 표시
          ...(data.partial === true ? { partial: true, stopped: typeof data.stopped === 'string' ? data.stopped : 'user' } : {}),
        };
      });
      setMessages(prev => {
        // 저장 못 한 턴(saveFailedIn)은 DB 목록에 없으므로 대화를 옮기기 전까지 화면 목록을 유지한다.
        const hasErrors = prev.some(msg => msg.error || msg.saveFailedIn);
        if (hasErrors) return prev;
        return messageList;
      });
    }, (snapshotError) => {
      // 남의 대화·지워진 대화는 읽을 수 없다. 앞 목록을 두면 그 대화가 화면에 남아 이어 보내게 된다.
      if (snapshotError?.code !== 'permission-denied') return;
      setMessages([]);
      setUnreadableConversationId(subscribedConversation);
      if (currentConversationRef.current === subscribedConversation) setCurrentConversation(null);
    });
    return () => unsubscribe();
  }, [currentUser, currentConversation, isAnonymous, userDetails, isStreaming, loadAnonymousMessages, setCurrentConversation]);
  
  // 익명 사용자를 위한 새 대화 생성
  const createAnonymousConversation = async (initialMessage) => {
    try {
      setLoading(true);
      
      // API에서 새 세션 초기화
      const sessionData = await initChatSession();
      const sessionId = sessionData.session_id;
      
      // 새 대화 ID 생성 (타임스탬프와 랜덤 문자열 조합)
      const conversationId = `anonymous_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      
      // 새 대화 생성
      const now = new Date();
      const newConversation = {
        id: conversationId,
        title: initialMessage.substring(0, 30) + (initialMessage.length > 30 ? '...' : ''),
        date: now.toLocaleDateString('ko-KR', {
          year: 'numeric', 
          month: 'long', 
          day: 'numeric'
        }),
        updatedAt: now, // 정렬용 타임스탬프 추가
        sessionId: sessionId,
        lastCategory: 'general', // 기본 카테고리
        lastCategoryName: '자유 대화', // 기본 카테고리명
        dismissedHandoffTargets: [],
        dismissedLibrarySuggestionFileIds: [],
      };
      
      // 🆕 PRO AI에서 선택된 모델이 있으면 저장
      if (selectedModel) {
        newConversation.selectedModel = selectedModel;
      }
      
      // 대화 목록 업데이트
      const updatedConversations = [newConversation, ...conversations];
      setConversations(updatedConversations);
      saveAnonymousConversations(updatedConversations);
      
      // 세션 매핑 업데이트
      setSessionMap(prevMap => ({
        ...prevMap,
        [conversationId]: sessionId
      }));
      
      // 현재 대화 설정
      setCurrentConversation(conversationId);
      
      return { conversationId, sessionId };
    } catch (err) {
      setError('익명 대화를 생성하는 중 오류가 발생했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // 새 대화 생성
  // waitForSave=false 면 문서 저장 확인을 기다리지 않고 id 와 saved(저장 Promise)를 바로 돌려준다.
  //   Firestore 첫 쓰기 확인은 연결이 차가우면 2~3초 걸린다. 호출부는 이 대화에 다른 문서를 쓰기 전에 saved 를 기다려야 한다.
  const createNewConversation = async (initialMessage, { waitForSave = true } = {}) => {
    // 익명 사용자인 경우
    if (isAnonymous && userDetails) {
      return createAnonymousConversation(initialMessage);
    }
    
    // Firebase 인증된 사용자인 경우
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }
    
    const viewAtStart = currentConversationRef.current;
    try {
      setLoading(true);
      
      // API에서 새 세션 초기화
      const sessionData = await initChatSession();
      const sessionId = sessionData.session_id;
      
      // 🆕 항상 GPT-4.1 모델 사용 (모델 전환 로직 제거)
      
      // 새 대화 문서 생성
      const conversationData = {
        userId: currentUser.uid,
        title: initialMessage.substring(0, 30) + (initialMessage.length > 30 ? '...' : ''),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sessionId: sessionId, // 세션 ID 저장
        lastCategory: 'general', // 기본 카테고리
        lastCategoryName: '자유 대화' // 기본 카테고리명
      };
      
      // 🆕 PRO AI에서 선택된 모델이 있으면 저장
      if (selectedModel) {
        conversationData.selectedModel = selectedModel;
      }
      
      const conversationRef = doc(collection(db, 'conversations'));
      const saved = _createConversationDoc(conversationData, { retries: 1, ref: conversationRef })
        .catch((saveError) => {
          throw _withSaveStage(saveError, 'create_conversation');
        });
      if (waitForSave) {
        await saved;
      } else {
        saved.catch(() => {});
      }
      
      // 🎯 [신규] 생성된 대화 ID로 즉시 현재 대화 상태를 업데이트합니다.
      //   만드는 사이 사용자가 다른 대화를 골랐으면 그 화면을 그대로 둔다(답은 새 대화에 저장된다).
      setSessionMap(prevMap => ({
        ...prevMap,
        [conversationRef.id]: sessionId
      }));
      if (currentConversationRef.current === viewAtStart) {
        setCurrentConversation(conversationRef.id);
      }
      
      return { conversationId: conversationRef.id, sessionId, saved };
    } catch (err) {
      setError('새 대화를 생성하는 중 오류가 발생했습니다.');
      throw _withSaveStage(err, 'create_conversation');
    } finally {
      // 🎯 스트리밍 중에는 로딩을 끄지 않음 (스트리밍 완료 시에만 끔)
      // setLoading(false);
    }
  };
  
  // 익명 사용자를 위한 메시지 전송
  const sendAnonymousMessage = async (content, conversationId = null) => {
    try {
      setLoading(true);
      
      // 이전 요청이 있다면 취소
      if (abortController) {
        abortController.abort();
      }
      
      // 새 AbortController 생성
      const controller = new AbortController();
      setAbortController(controller);
      
      // 메시지 카운트 증가 (제한 없이 카운팅만 함)
      incrementAnonymousMessageCount();
      
      // 대화 ID 확인 (없으면 새로 생성)
      let activeConversationId = conversationId;
      let isNewConversation = false;
      let sessionId;
      
      // 🎯 [수정] 익명 사용자도 "선 생성" 원칙을 적용합니다.
      if (!activeConversationId) {
        const result = await createAnonymousConversation(content);
        activeConversationId = result.conversationId;
        sessionId = result.sessionId;
        isNewConversation = true;
      } else {
        sessionId = sessionMap[activeConversationId];
        
        // 세션 ID가 없는 경우 다시 가져오기 시도
        if (!sessionId) {
          try {
            // 기존 세션 ID 확인 (이전 대화가 있는 경우)
            const conversationData = conversations.find(conv => conv.id === activeConversationId);
            if (conversationData && conversationData.sessionId) {
              sessionId = conversationData.sessionId;
            } else {
              // 세션 ID가 없으면 새로 생성
              const sessionData = await initChatSession();
              sessionId = sessionData.session_id;
              
              // 세션 매핑 업데이트
              setSessionMap(prevMap => ({
                ...prevMap,
                [activeConversationId]: sessionId
              }));
              
              // 대화 목록 업데이트
              const updatedConversations = conversations.map(conv => 
                conv.id === activeConversationId ? { ...conv, sessionId } : conv
              );
              setConversations(updatedConversations);
              saveAnonymousConversations(updatedConversations);
            }
          } catch (err) {
            console.error('세션 ID 가져오기 오류:', err);
            throw new Error('메시지를 전송하는 중 오류가 발생했습니다.');
          }
        }
      }
      
      // 사용자 메시지 ID 생성
      const userMessageId = `anonymous_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      
      // 사용자 메시지 생성
      const userMessage = {
        id: userMessageId,
        content,
        role: 'user',
        timestamp: new Date()
      };
      
      // 현재 메시지 목록에 사용자 메시지 추가
      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);
      
      // 로컬 스토리지에 메시지 저장
      saveAnonymousMessages(activeConversationId, updatedMessages);
      
      // 대화 제목 업데이트 (새 대화의 경우)
      if (isNewConversation) {
        // 대화 목록은 이미 createAnonymousConversation에서 업데이트됨
      } else {
        // 기존 대화 날짜만 업데이트 (제목은 유지)
        const updatedConversations = conversations.map(conv => {
          if (conv.id === activeConversationId) {
            return { 
              ...conv, 
              date: new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', 
                month: 'long', 
                day: 'numeric'
              })
            };
          }
          return conv;
        });
        
        // 대화 목록 업데이트
        setConversations(updatedConversations);
        saveAnonymousConversations(updatedConversations);
      }
      
      // 현재 대화 ID 설정
      setCurrentConversation(activeConversationId);
      
      try {
        // AI 응답 요청 (취소 가능한 요청)
        const response = await getAIResponseWithSignal(content, sessionId, controller.signal);
        
        // AI 메시지 ID 생성
        const aiMessageId = `anonymous_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
        
        // AI 메시지 생성
        const aiMessage = {
          id: aiMessageId,
          content: response.response,
          role: 'assistant',
          timestamp: new Date()
        };
        
        // 메시지 목록에 AI 메시지 추가
        const finalMessages = [...updatedMessages, aiMessage];
        setMessages(finalMessages);
        
        // 로컬 스토리지에 메시지 저장
        saveAnonymousMessages(activeConversationId, finalMessages);
        
        // AbortController 초기화
        setAbortController(null);
        
        // 로딩 상태 해제
        setLoading(false);
        
        return { 
          userMessageId, 
          aiMessageId, 
          conversationId: activeConversationId,
          isNewConversation
        };
      } catch (apiError) {
        // 요청이 중단된 경우 별도 처리
        if (apiError.name === 'AbortError') {
          return { 
            userMessageId,
            conversationId: activeConversationId,
            isNewConversation,
            aborted: true
          };
        }
        
        console.error('AI 응답 처리 오류:', apiError);
        
        // API 오류 시에도 AI 메시지를 추가하여 대화 흐름 유지
        const aiMessageId = `anonymous_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
        
        // Rate Limit 에러인 경우 통일된 메시지 사용
        const errorContent = isRateLimitError(apiError) 
          ? getRateLimitMessage({ includeLink: true, html: false })
          : '죄송합니다. 일시적인 문제로 응답을 생성할 수 없습니다. 잠시 후 다시 시도해주세요.';
        
        const aiMessage = {
          id: aiMessageId,
          content: errorContent,
          role: 'assistant',
          timestamp: new Date()
        };
        
        // 메시지 목록에 AI 메시지 추가
        const finalMessages = [...updatedMessages, aiMessage];
        setMessages(finalMessages);
        
        // 로컬 스토리지에 메시지 저장
        saveAnonymousMessages(activeConversationId, finalMessages);
        
        // AbortController 초기화
        setAbortController(null);
        
        return { 
          userMessageId, 
          aiMessageId, 
          conversationId: activeConversationId,
          isNewConversation,
          error: apiError 
        };
      }
    } catch (err) {
      console.error('익명 메시지 전송 오류:', err);
      setError(err.message || '메시지를 전송하는 중 오류가 발생했습니다.');
      // 오류 발생 시에는 로딩 상태 해제
      setLoading(false);
      // AbortController 초기화
      setAbortController(null);
      throw err;
    }
  };
  
  // 메시지 전송
  const sendMessage = async (content, conversationId = null) => {
    // 익명 사용자인 경우
    if (isAnonymous && userDetails) {
      return sendAnonymousMessage(content, conversationId);
    }
    
    // Firebase 인증된 사용자인 경우
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }
    
    if (!content.trim()) {
      throw new Error('메시지 내용을 입력해주세요.');
    }
    
    try {
      // 로딩 상태 설정
      setLoading(true);

      // 이전 요청이 있다면 취소
      if (abortController) {
        abortController.abort();
      }
      
      // 새 AbortController 생성
      const controller = new AbortController();
      setAbortController(controller);
      
      // 대화 ID 확인 (없으면 새로 생성)
      let activeConversationId = conversationId;
      let isNewConversation = false;
      let sessionId;
      
      if (!activeConversationId) {
        const result = await createNewConversation(content);
        activeConversationId = result.conversationId;
        sessionId = result.sessionId;
        isNewConversation = true;
      } else {
        sessionId = sessionMap[activeConversationId];
        
        // 세션 ID가 없는 경우 다시 가져오기 시도
        if (!sessionId) {
          try {
            // 대화 문서 조회
            const conversationDoc = await getDoc(doc(db, 'conversations', activeConversationId));
            
            if (conversationDoc.exists() && conversationDoc.data().sessionId) {
              sessionId = conversationDoc.data().sessionId;
              
              // 세션 매핑 업데이트
              setSessionMap(prevMap => ({
                ...prevMap,
                [activeConversationId]: sessionId
              }));
            } else {
              // 세션 ID가 없으면 새로 생성
              const sessionData = await initChatSession();
              sessionId = sessionData.session_id;
              
              // 대화 문서 업데이트
              await updateDoc(doc(db, 'conversations', activeConversationId), {
                sessionId: sessionId
              });
              
              // 세션 매핑 업데이트
              setSessionMap(prevMap => ({
                ...prevMap,
                [activeConversationId]: sessionId
              }));
            }
          } catch (err) {
            console.error('세션 ID 가져오기 오류:', err);
            throw new Error('메시지를 전송하는 중 오류가 발생했습니다.');
          }
        }
      }
      
      // 사용자 메시지 추가
      const userMessageRef = await _createMessageDoc(activeConversationId, {
        content,
        role: 'user',
        timestamp: serverTimestamp()
      });
      
      // 대화 문서 타임스탬프 업데이트 및 제목 설정 (새 대화인 경우)
      if (isNewConversation) {
        // 이미 createNewConversation에서 처리됨
      } else {
        // 기존 대화 업데이트 - 제목은 업데이트하지 않고 타임스탬프만 업데이트
        await updateDoc(doc(db, 'conversations', activeConversationId), {
          updatedAt: serverTimestamp()
        });
      }
      
      // 현재 대화 ID 설정
      setCurrentConversation(activeConversationId);
      
      try {
        // AI 응답 요청 (취소 가능한 요청)
        const response = await getAIResponseWithSignal(content, sessionId, controller.signal);
        
        // AI 메시지 추가
        const aiMessageRef = await _createMessageDoc(activeConversationId, {
          content: response.response,
          role: 'assistant',
          timestamp: serverTimestamp()
        });
        
        // AbortController 초기화
        setAbortController(null);
        
        // 로딩 상태 해제
        setLoading(false);
        
        return { 
          userMessageId: userMessageRef.id, 
          aiMessageId: aiMessageRef.id, 
          conversationId: activeConversationId,
          isNewConversation
        };
      } catch (apiError) {
        // 요청이 중단된 경우 별도 처리
        if (apiError.name === 'AbortError') {
          return { 
            userMessageId: userMessageRef.id,
            conversationId: activeConversationId,
            isNewConversation,
            aborted: true
          };
        }
        
        console.error('AI 응답 처리 오류:', apiError);
        
        // Rate Limit 에러인 경우 통일된 메시지 사용
        const errorContent = isRateLimitError(apiError) 
          ? getRateLimitMessage({ includeLink: true, html: false })
          : '죄송합니다. 일시적인 문제로 응답을 생성할 수 없습니다. 잠시 후 다시 시도해주세요.';
        
        // API 오류 시에도 AI 메시지를 추가하여 대화 흐름 유지
        const aiMessageRef = await _createMessageDoc(activeConversationId, {
          content: errorContent,
          role: 'assistant',
          timestamp: serverTimestamp()
        });
        
        // 현재 대화 ID 설정
        setCurrentConversation(activeConversationId);
        
        // AbortController 초기화
        setAbortController(null);
        
        return { 
          userMessageId: userMessageRef.id, 
          aiMessageId: aiMessageRef.id, 
          conversationId: activeConversationId,
          isNewConversation,
          error: apiError 
        };
      }
    } catch (err) {
      console.error('메시지 전송 오류:', err);
      setError('메시지를 전송하는 중 오류가 발생했습니다.');
      // 오류 발생 시에는 로딩 상태 해제
      setLoading(false);
      // AbortController 초기화
      setAbortController(null);
      throw err;
    } finally {
      // 여기서 로딩 상태를 해제하지 않고 ChatPage.js에서 처리
      // setLoading(false);
    }
  };
  
  // 응답 생성 중지 함수
  const stopResponseGeneration = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setLoading(false);
    }
  };

  // 🆕 세션 매핑 업데이트 함수 (모델 전환용)
  const updateSessionMapping = (conversationId, sessionId) => {
    setSessionMap(prevMap => ({
      ...prevMap,
      [conversationId]: sessionId
    }));
  };
  
  // 커스텀 getAIResponse 함수 (AbortSignal 포함) - 🔧 기존 동기 방식
  const getAIResponseWithSignal = async (message, sessionId, signal, category = 'general') => {
    try {
      // API 요청 데이터 구성
      const requestData = {
        message: message,
        session_id: sessionId,
        category: category, // 🆕 선택된 카테고리 전달
        selected_model: toChatApiModelId(selectedModel) // PRO AI용 wire contract
      };
      
      // API 엔드포인트 구성 - 중앙집중식 설정 사용
      const API_BASE_URL = getApiBaseUrl();
      
      const endpoint = `${API_BASE_URL}/api/chat/message`;
      
      // API 요청 (AbortSignal 포함)
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData),
        credentials: 'omit',
        mode: 'cors',
        signal // AbortSignal 전달
      });
      
      // 요청 실패 처리
      if (!response.ok) {
        const errorText = await response.text();
        console.error('AI 응답 오류:', response.status, errorText);
        throw new Error(`AI 응답을 가져오는 중 오류가 발생했습니다. 상태 코드: ${response.status}`);
      }
      
      // 응답 파싱
      const responseData = await response.json();
      return {
        session_id: responseData.session_id,
        response: responseData.last_message.content,
        messages: responseData.messages
      };
    } catch (error) {
      console.error('AI 응답 요청 실패:', error);
      throw error;
    }
  };

  // 🚀 새로운 스트리밍 메시지 전송 함수 (카테고리 지원 추가, 파일 컨텍스트 지원)
  // artifactState: 학습지/PPT 산출물 현황 요약 ({has_artifact, latest, available_formats, mode}).
  // 백엔드가 "파일 어디 있어" 류 질문에 정확히 답하고 재생성 범위를 판단하는 데 쓴다. 아직 무시해도 무해.
  // newlyAttachedFileIds: 이번 메시지에서 새로 첨부한 파일 ID (attachedFileIds 의 부분집합, 익명은 파일 첨부 불가)
  // 📺 전송 전 준비 단계(유튜브 영상 읽기 등)에도 사용자 말풍선과 답변 자리를 먼저 보여주기 위한 자리표시자.
  //   beginPendingTurn → (준비 작업, updatePendingTurnStatus) → sendMessageStream 이 같은 자리를 이어받는다.
  //   로그인 사용자 스트림(sendFirebaseMessageStream)에서만 이어받는다(익명 흐름은 준비 단계가 없다).
  const pendingTurnRef = useRef(null);
  const beginPendingTurn = useCallback((content, { category = 'general', modelId = null, statusMessage = '', statusType = 'preparing' } = {}) => {
    const stamp = Date.now();
    const pending = { userId: `pending_user_${stamp}`, aiId: `pending_ai_${stamp + 1}` };
    pendingTurnRef.current = pending;
    setLoading(true);
    setError(null);
    setMessages(prevMessages => [
      ...prevMessages,
      { id: pending.userId, role: 'user', content, timestamp: new Date() },
      {
        id: pending.aiId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        streaming: true,
        category,
        modelId: modelId || 'gpt-5.4-nano',
        isSearching: true,
        currentStatus: statusMessage ? { type: statusType, message: statusMessage } : null,
        web_search_sources: [],
        artifacts: [],
      },
    ]);
    return pending;
  }, []);

  const updatePendingTurnStatus = useCallback((statusMessage, statusType = 'preparing') => {
    const pending = pendingTurnRef.current;
    if (!pending) return;
    setMessages(prevMessages => prevMessages.map(msg => (
      msg.id === pending.aiId
        ? { ...msg, currentStatus: { type: statusType, message: statusMessage }, isSearching: true, streaming: true }
        : msg
    )));
  }, []);

  const cancelPendingTurn = useCallback(() => {
    const pending = pendingTurnRef.current;
    if (!pending) return;
    pendingTurnRef.current = null;
    setMessages(prevMessages => prevMessages.filter(msg => msg.id !== pending.userId && msg.id !== pending.aiId));
    setLoading(false);
  }, []);

  // 자리표시자가 있으면 같은 위치에서 실제 말풍선으로 교체(같은 커밋에서 바뀌어 깜빡임 없음)
  const takeOverPendingTurn = (userMessage, aiMessage) => {
    const pending = pendingTurnRef.current;
    pendingTurnRef.current = null;
    setMessages(prevMessages => {
      const base = pending
        ? prevMessages.filter(msg => msg.id !== pending.userId && msg.id !== pending.aiId)
        : prevMessages;
      return [...base, userMessage, aiMessage];
    });
  };

  // attachmentMeta: buildAttachmentMeta() 결과. 사용자 메시지 문서에 첨부 계측 필드로 저장된다(요청 본문에는 쓰지 않음).
  // turnReplace: 마지막 턴 바꿔 보내기 { action, user, ai, replaceDocIds } (utils/chatTurnReplace)
  const sendMessageStream = async (content, conversationId = null, onStreamChunk, onStreamComplete, onStreamError, category = 'general', analysisContext = null, selectedModel = null, includeAttachedFiles = false, attachedFileIds = null, existingOutline = null, workspaceTargetFileId = null, artifactState = null, newlyAttachedFileIds = null, attachmentMeta = null, skillContext = null, turnReplace = null) => {
    // 익명 사용자인 경우 (userDetails 체크 제거 - isAnonymous만으로 판별)
    if (isAnonymous) {
      return sendAnonymousMessageStream(content, conversationId, onStreamChunk, onStreamComplete, onStreamError, category, analysisContext, selectedModel, includeAttachedFiles, attachedFileIds, existingOutline, artifactState, turnReplace);
    }
    
    // Firebase 인증된 사용자인 경우
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }
    
    // 🚀 Firebase 사용자 스트리밍 구현
    return sendFirebaseMessageStream(content, conversationId, onStreamChunk, onStreamComplete, onStreamError, category, analysisContext, selectedModel, includeAttachedFiles, attachedFileIds, existingOutline, workspaceTargetFileId, artifactState, newlyAttachedFileIds, attachmentMeta, skillContext, turnReplace);
  };

  // 🚀 Firebase 사용자 스트리밍 메시지 전송 (단순화 버전, 카테고리 지원 추가, 파일 컨텍스트 지원)
  const sendFirebaseMessageStream = async (content, conversationId = null, onStreamChunk, onStreamComplete, onStreamError, category = 'general', analysisContext = null, selectedModel = null, includeAttachedFiles = false, attachedFileIds = null, existingOutline = null, workspaceTargetFileId = null, artifactState = null, newlyAttachedFileIds = null, attachmentMeta = null, skillContext = null, turnReplace = null) => {
    if (isAnonymousConversationId(conversationId)) {
      conversationId = null;
    }
    const viewAtStart = currentConversationRef.current;
    const sourceArtifactId = getLatestChatArtifactId(messages, content);
    const userMessage = {
      id: `user_${Date.now()}`,
      content: content,
      role: 'user',
      timestamp: new Date(),
      ...(attachmentMeta?.newlyAttachedFileIds?.length > 0 && {
        newlyAttachedFileIds: attachmentMeta.newlyAttachedFileIds,
        attachedFileIds: attachmentMeta.attachedFileIds,
        attachedFileNames: attachmentMeta.attachedFileNames,
      }),
    };
    // 바꿔 보내기는 옛 턴이 있는 대화에서만 한다(새 대화를 만들지 않는다).
    const replace = turnReplace && conversationId ? turnReplace : null;
    const restoreOldTurn = () => setMessages((previousMessages) => (
      restoreReplacedTurn(previousMessages, replace, [userMessage.id, aiMessage.id])
    ));
    
    const aiMessage = {
      id: `ai_${Date.now() + 1}`,
      content: '',
      role: 'assistant',
      timestamp: new Date(),
      streaming: true,
      category: category,
      modelId: selectedModel || 'gpt-5.4-nano',
      isSearching: true,
      thinkingStatus: 'analyzing',
      web_search_queries: [],
      web_search_sources: [],
      reasoning_steps: [],
      artifacts: []
    };
    
    try {
      setLoading(true);
      setError(null);
      setIsStreaming(true);
      
      // 즉시 화면에 표시 (Optimistic Update) — 준비 단계 자리표시자가 있으면 그 자리를 이어받는다
      if (replace) {
        pendingTurnRef.current = null;
        setMessages((previousMessages) => swapReplacedTurn(previousMessages, replace, userMessage, aiMessage));
      } else {
        takeOverPendingTurn(userMessage, aiMessage);
      }
      
      let activeConversationId = conversationId;
      let sessionId = conversationId ? sessionMap[conversationId] : null;
      let isNewConversation = false;
      let conversationSaved = Promise.resolve();
      
      if (!activeConversationId) {
        const newConversation = await createNewConversation(content, { waitForSave: false });
        activeConversationId = newConversation.conversationId;
        sessionId = newConversation.sessionId;
        conversationSaved = newConversation.saved;
        isNewConversation = true;
      } else if (!sessionId) {
        try {
          const conversationDoc = await getDoc(doc(db, 'conversations', activeConversationId));
          if (conversationDoc.exists() && conversationDoc.data().sessionId) {
            sessionId = conversationDoc.data().sessionId;
          } else {
            const sessionData = await initChatSession();
            sessionId = sessionData.session_id;
          }
          
          setSessionMap(prevMap => ({
            ...prevMap,
            [activeConversationId]: sessionId
          }));
        } catch (err) {
          console.error('❌ 세션 ID 가져오기 실패:', err);
          throw new Error('세션을 생성할 수 없습니다.');
        }
      }
      
      if (currentConversationRef.current === viewAtStart) {
        setCurrentConversation(activeConversationId);
      }
      
      // AbortController 생성
      const controller = new AbortController();
      setAbortController(controller);
      
      // 🃏 이 턴에 온 되묻기 카드 / 가정 칩 — complete 시 메시지 문서에 함께 저장한다
      let streamedClarifyCard = null;
      let streamedClarifyAssumptions = [];
      // 🖼️ 이 턴의 인라인 이미지 스트립(인덱스별 pending/ok/empty) — complete 의 web_images 가 최종본
      let streamedWebImages = [];
      // 🃏 이 턴의 사진 추천 카드(인덱스별 pending/ok/empty) — complete 의 rich_blocks 가 최종본
      let streamedRichBlocks = [];
      // ✍️ 본문 청크는 화면 프레임마다 마지막 값만 반영한다
      const contentBatcher = createLatestFrameBatcher((cleanContent) => {
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            msg.id === aiMessage.id
              ? {
                  ...msg,
                  content: cleanContent,
                  currentStatus: null,
                  streaming: true,
                  isSearching: false,
                  // 구형 백엔드 호환: 첫 줄 대기 문장으로도 파일 예정을 감지
                  ...(detectPendingArtifactFromContent(msg, cleanContent) || {})
                }
              : msg
          )
        );
      });
      
      // 스트리밍 응답 처리 (카테고리 정보 및 분석 컨텍스트 포함)
      await getAIResponseStream(
        content,
        sessionId,
        category,
        analysisContext,
        // onChunk
        (chunkData) => {
          if (chunkData.type !== 'content') contentBatcher.flush();
          // 🎯 첫 번째 응답이 오기 시작할 때까지 로딩 유지
          if (chunkData.type === 'start') {
            //console.log('🚀 AI 응답 스트리밍 시작됨');
            // 로딩은 계속 유지 (아직 setLoading(false) 하지 않음)
          } else if (chunkData.type === 'clarify_card' && chunkData.card && typeof chunkData.card === 'object') {
            // 🃏 첫 턴 되묻기 카드: 본문 대신 카드가 그려진다 (스트리밍 상태 종료)
            streamedClarifyCard = chunkData.card;
            setMessages(prevMessages =>
              prevMessages.map(msg =>
                msg.id === aiMessage.id
                  ? { ...msg, clarify_card: chunkData.card, currentStatus: null, isSearching: false }
                  : msg
              )
            );
          } else if (chunkData.type === 'rich_block' && Number.isInteger(chunkData.index)) {
            streamedRichBlocks = mergeRichBlock(streamedRichBlocks, chunkData);
            const nextBlocks = streamedRichBlocks;
            setMessages(prevMessages =>
              prevMessages.map(msg =>
                msg.id === aiMessage.id
                  ? { ...msg, rich_blocks: nextBlocks }
                  : msg
              )
            );
          } else if (chunkData.type === 'image_strip' && Number.isInteger(chunkData.index)) {
            // 🖼️ 인라인 이미지 스트립: pending(스켈레톤) → ok(이미지) / empty(자리 제거). 인덱스별로 누적한다.
            streamedWebImages = mergeWebImageStrip(streamedWebImages, chunkData);
            const nextStrips = streamedWebImages;
            setMessages(prevMessages =>
              prevMessages.map(msg =>
                msg.id === aiMessage.id
                  ? { ...msg, web_images: nextStrips }
                  : msg
              )
            );
          } else if (chunkData.type === 'clarify_assumptions' && Array.isArray(chunkData.assumptions)) {
            // 🃏 묻지 않고 진행하는 기본 가정 — 답변 위에 칩으로 투명하게 보여준다
            streamedClarifyAssumptions = chunkData.assumptions
              .filter((item) => item && typeof item === 'object' && typeof item.label === 'string' && item.label)
              .slice(0, 3)
              .map((item) => ({
                key: String(item.key || ''),
                label: item.label,
                // 학습된 기본값 칩('○○ 기준이 기본이에요'): 누르면 서버 기본값을 지운다
                ...(item.action === 'clear_default' && item.field ? { action: 'clear_default', field: String(item.field) } : {}),
              }));
            setMessages(prevMessages =>
              prevMessages.map(msg =>
                msg.id === aiMessage.id
                  ? { ...msg, clarify_assumptions: streamedClarifyAssumptions }
                  : msg
              )
            );
          } else if (isArtifactProgressChunk(chunkData)) {
            // 📎 파일 생성 진행 카드: planned/generating/result 는 pendingArtifact 하나로 이어진다
            setMessages(prevMessages =>
              prevMessages.map(msg =>
                msg.id === aiMessage.id
                  ? { ...msg, ...applyArtifactChunk(msg, chunkData) }
                  : msg
              )
            );
          } else if (chunkData.type === 'status' && !chunkData.quiet) {
            // 🆕 AI 상태 업데이트 (thinking, web search 등). quiet 안내는 칩 라벨만 바꾼다(ChatPage).
            setMessages(prevMessages => 
              prevMessages.map(msg => 
                msg.id === aiMessage.id 
                  ? { 
                      ...msg, 
                      currentStatus: {
                        type: chunkData.statusType,
                        message: chunkData.message,
                        icon: chunkData.icon,
                        detail: chunkData.detail
                      },
                      ...(chunkData.steps?.length
                        ? { activitySteps: appendActivitySteps(msg.activitySteps, chunkData.steps) }
                        : {}),
                      streaming: true,
                      isSearching: true
                    }
                  : msg
              )
            );
          } else if (chunkData.type === 'content') {
            contentBatcher.schedule(_stripSystemTags(chunkData.fullContent || chunkData.chunk || ''));
          }
          
          if (onStreamChunk) {
            onStreamChunk(chunkData);
          }
        },
        // onComplete - 이때 DB에 저장
        async (completeData) => {
          // onComplete 콜백
          contentBatcher.flush();
          let saveStage = null;
          try {
            const keptPartial = completeData.aborted && completeData.keepPartial
              ? _stripSystemTags(completeData.fullResponse || '').trim()
              : '';
            if (keptPartial) {
              // 멈춤: 받은 만큼은 남긴다. 서버도 같은 문서 id 로 확인 저장하므로 어느 쪽이 먼저든 한 벌만 남는다.
              const turnDocIds = turnMessageDocIds(completeData.turnId);
              setMessages(prevMessages => prevMessages.map(msg => (
                msg.id === aiMessage.id
                  ? {
                      ...msg,
                      content: keptPartial,
                      streaming: false,
                      isSearching: false,
                      currentStatus: null,
                      stopped: 'user',
                      partial: true,
                    }
                  : msg
              )));
              const finishStreaming = () => {
                setLoading(false);
                setAbortController(null);
                setIsStreaming(false);
              };
              // 목록 구독은 저장이 끝난 뒤 다시 켠다 — 먼저 켜면 아직 없는 멈춘 답(바꿔 보낸 턴은 옛 턴)이 DB 목록으로 덮인다.
              let aiMessageRef = null;
              try {
                saveStage = 'create_conversation';
                await conversationSaved;
                const partialUserData = {
                  content,
                  role: 'user',
                  timestamp: serverTimestamp(),
                  ...attachmentMetaToMessageFields(attachmentMeta),
                };
                const partialAiData = {
                  content: keptPartial,
                  role: 'assistant',
                  timestamp: serverTimestamp(),
                  category,
                  modelId: normalizeChatModelId(selectedModel || 'gpt-5.4-nano'),
                  requestedModelId: selectedModel || null,
                  stopped: 'user',
                  partial: true,
                };
                if (replace) {
                  saveStage = 'save_replace';
                  aiMessageRef = await _commitReplacedTurnDocs(activeConversationId, {
                    user: { id: turnDocIds?.user, data: partialUserData },
                    assistant: { id: turnDocIds?.assistant, data: partialAiData },
                    deleteIds: replace.replaceDocIds || [],
                  });
                } else {
                  saveStage = 'save_user';
                  await _createMessageDoc(activeConversationId, partialUserData, { retries: 1, id: turnDocIds?.user });
                  saveStage = 'save_ai';
                  aiMessageRef = await _createMessageDoc(activeConversationId, partialAiData, { retries: 1, id: turnDocIds?.assistant });
                }
                saveStage = 'update_conversation';
                await updateDoc(doc(db, 'conversations', activeConversationId), {
                  updatedAt: serverTimestamp(),
                  lastCategory: category || 'general',
                  lastCategoryName: CATEGORY_NAME_MAP[category] || CATEGORY_NAME_MAP['general'],
                });
                saveStage = null;
              } catch (partialSaveError) {
                console.warn('멈춘 답을 저장하지 못했어요(서버가 같은 id 로 다시 확인 저장):', partialSaveError);
                trackChatUxEvent('save_failed', {
                  category: category || 'general',
                  errorCode: getTrackingErrorCode(partialSaveError),
                  isAnonymous: false,
                  isPro: subscriptionStatus?.isPro === true,
                  model: selectedModel,
                  stage: `partial_${saveStage || 'unknown'}`,
                });
                const saveFailedIn = currentConversationRef.current === activeConversationId
                  ? activeConversationId
                  : null;
                if (saveFailedIn) {
                  setMessages(prevMessages => prevMessages.map(msg => (
                    msg.id === aiMessage.id || msg.id === userMessage.id ? { ...msg, saveFailedIn } : msg
                  )));
                }
              }
              finishStreaming();
              if (onStreamComplete) {
                onStreamComplete({
                  ...completeData,
                  keptPartial: true,
                  conversationId: activeConversationId,
                  sessionId,
                  aiMessageId: aiMessage.id,
                  aiMessageDocId: aiMessageRef?.id || aiMessage.id,
                  isNewConversation,
                });
              }
              return;
            }
            if (completeData.aborted) {
              if (replace) {
                restoreOldTurn();
              } else {
                setMessages((previousMessages) => removeOptimisticTurn(
                  previousMessages,
                  userMessage.id,
                  aiMessage.id
                ));
              }
              setLoading(false);
              setAbortController(null);
              setIsStreaming(false);

              if (isNewConversation) {
                setConversations((previousConversations) => (
                  previousConversations.filter(
                    (conversation) => conversation.id !== activeConversationId
                  )
                ));
                setSessionMap((previousMap) => {
                  const nextMap = { ...previousMap };
                  delete nextMap[activeConversationId];
                  return nextMap;
                });
                if (currentConversationRef.current === activeConversationId) {
                  setCurrentConversation(null);
                }

                try {
                  await conversationSaved.catch(() => {});
                  const batch = writeBatch(db);
                  batch.delete(doc(db, 'conversations', activeConversationId));
                  await batch.commit();
                } catch (cleanupError) {
                  console.warn(
                    '중단된 빈 대화를 정리하지 못했어요:',
                    cleanupError
                  );
                }
              }

              if (onStreamComplete) {
                onStreamComplete({
                  ...completeData,
                  conversationId: isNewConversation
                    ? null
                    : activeConversationId,
                  sessionId,
                  aiMessageId: aiMessage.id,
                  isNewConversation,
                });
              }
              return;
            }

            const effectiveModel = normalizeChatModelId(
              completeData.effective_model || selectedModel || 'gpt-5.4-nano'
            );
            // 서버 체인 복구는 effective_model 을 실제 runtime 모델과 비교한다. UI ID로 바꿔 저장하면 체인이 끊긴다.
            const runtimeModel = completeData.effective_model || effectiveModel;
            const conversationModel = toPersistedChatSelection(
              selectedModel,
              effectiveModel
            );
            // ❗ "선 생성" 원칙에 따라, 새 대화 생성 로직은 더 이상 필요 없습니다.
            // activeConversationId는 항상 유효한 DB ID를 가집니다.

            //console.log(`💾 DB에 메시지 저장 시도: 대화 ID=${activeConversationId}`);
            saveStage = 'create_conversation';
            await conversationSaved;
            const turnDocIds = turnMessageDocIds(completeData.turnId);
            const userMessageData = {
              content: content,
              role: 'user',
              timestamp: serverTimestamp(),
              ...attachmentMetaToMessageFields(attachmentMeta),
            };
            // 1. 사용자 메시지를 DB에 저장합니다. (바꿔 보내기는 아래 답변과 한 배치로 저장)
            if (!replace) {
              saveStage = 'save_user';
              await _createMessageDoc(activeConversationId, userMessageData, { retries: 1, id: turnDocIds?.user });
            }
            
            // 2. AI 메시지를 DB에 저장합니다.
            // 🆕 Responses API 체인 정보도 함께 저장 (서버 재시작 후 복구용)
            const aiMessageData = {
              content: _stripSystemTags(completeData.fullResponse || '').trim(),
              role: 'assistant',
              timestamp: serverTimestamp(),
              category: category,
              modelId: effectiveModel,
              effective_model: runtimeModel,
              actual_response_model: completeData.actual_response_model || null,
              requestedModelId: selectedModel || null,
              reasoningEffort: completeData.reasoning_effort || null
            };
            
            // GPT-5 등 Responses API를 사용한 경우 체인 정보 저장
            if (completeData.response_id) {
              aiMessageData.response_id = completeData.response_id;
              aiMessageData.response_chain_len = completeData.response_chain_len || 0;
            }
            
            // 🌐 웹 서치 데이터 저장 (GPT-5.1 등)
            if (completeData.web_search_queries) {
              aiMessageData.web_search_queries = completeData.web_search_queries;
            }
            if (completeData.web_search_sources) {
              aiMessageData.web_search_sources = completeData.web_search_sources;
            }
            // 🧠 추론 과정 저장 (PRO AI 전용)
            if (completeData.reasoning_steps) {
              aiMessageData.reasoning_steps = completeData.reasoning_steps;
            }
            if (completeData.memory_update) {
              aiMessageData.memory_update = completeData.memory_update;
            }
            const completeSuggestions = _normalizeSuggestions(completeData.suggestions);
            if (completeSuggestions.length > 0) {
              aiMessageData.suggestions = completeSuggestions;
              aiMessageData.suggestion_results = {};
            }
            const completePersonalizationUsed = _normalizePersonalizationUsed(
              completeData.personalization_used
            );
            if (completePersonalizationUsed) {
              aiMessageData.personalization_used = completePersonalizationUsed;
            }
            const completeLibraryReferences = _normalizeLibraryReferences(completeData.library_references);
            if (completeLibraryReferences.length > 0) {
              aiMessageData.library_references = completeLibraryReferences;
            }
            const completePastChatReferences = _normalizePastChatReferences(completeData.past_chat_references);
            if (completePastChatReferences.length > 0) {
              aiMessageData.past_chat_references = completePastChatReferences;
            }
            if (Array.isArray(completeData.artifacts) && completeData.artifacts.length > 0) {
              aiMessageData.artifacts = completeData.artifacts;
            }
            const completePromiseWithoutFile = completeData.promise_without_file === true
              && !(Array.isArray(completeData.artifacts) && completeData.artifacts.length > 0);
            if (completePromiseWithoutFile) {
              aiMessageData.promise_without_file = true;
            }
            const completeEditNotApplied = completeData.edit_not_applied === true;
            const completeEditTarget = completeEditNotApplied && completeData.edit_not_applied_target === 'artifact'
              ? 'artifact'
              : undefined;
            if (completeEditNotApplied) {
              aiMessageData.edit_not_applied = true;
              if (completeEditTarget) aiMessageData.edit_not_applied_target = completeEditTarget;
            }
            const completeActionNotTaken = completeData.action_not_taken === true;
            if (completeActionNotTaken) {
              aiMessageData.action_not_taken = true;
            }
            // 🃏 되묻기 카드 / 가정 칩 — 새로고침 후에도 카드·칩이 유지되도록 메시지 문서에 저장
            const completeClarifyCard = (completeData.clarify_card && typeof completeData.clarify_card === 'object')
              ? completeData.clarify_card
              : streamedClarifyCard;
            if (completeClarifyCard) {
              aiMessageData.clarify_card = completeClarifyCard;
              aiMessageData.clarify_result = null;
            }
            if (streamedClarifyAssumptions.length > 0) {
              aiMessageData.clarify_assumptions = streamedClarifyAssumptions;
            }
            // 🧩 P5 자동 스킬 라벨 — 메시지 문서에 저장해 새로고침·다른 기기에서도 라벨과 '대화 안 유지'가 이어진다
            const completeAppliedSkill = (completeData.applied_skill && typeof completeData.applied_skill === 'object' && completeData.applied_skill.skill)
              ? completeData.applied_skill
              : null;
            if (completeAppliedSkill) {
              aiMessageData.applied_skill = completeAppliedSkill;
            }
            // 🖼️ 인라인 이미지 스트립 — complete.web_images 가 최종본(ok 만). 없으면 스트리밍 중 받은 ok 만 남긴다.
            const completeWebImages = finalizeWebImages(completeData.web_images, streamedWebImages);
            if (completeWebImages.length > 0) {
              aiMessageData.web_images = completeWebImages;
            }
            const completeRichBlocks = finalizeRichBlocks(completeData.rich_blocks, streamedRichBlocks);
            if (completeRichBlocks.length > 0) {
              aiMessageData.rich_blocks = completeRichBlocks;
            }
            const completeFollowupForm = completeData.followup_form || null;
            if (completeFollowupForm) {
              aiMessageData.followup_form = completeFollowupForm;
            }
            const completeNextChips = Array.isArray(completeData.next_chips) ? completeData.next_chips : [];
            if (completeNextChips.length > 0) {
              aiMessageData.next_chips = completeNextChips;
            }
            
            // 메시지 문서 ID를 로컬 메시지에도 반영해 onSnapshot 재구독 전에도
            // 제안 카드 응답(resolveSuggestion)을 정확한 문서에 기록할 수 있게 한다.
            saveStage = replace ? 'save_replace' : 'save_ai';
            const aiMessageRef = replace
              ? await _commitReplacedTurnDocs(activeConversationId, {
                user: { id: turnDocIds?.user, data: userMessageData },
                assistant: { id: turnDocIds?.assistant, data: aiMessageData },
                deleteIds: replace.replaceDocIds || [],
              })
              : await _createMessageDoc(activeConversationId, aiMessageData, {
                retries: 1,
                id: turnDocIds?.assistant,
              });
            //console.log('✅ DB에 메시지 저장 성공!');
            
            // 3. 대화 정보를 갱신합니다 (새 대화든 기존 대화든 실제 사용된 카테고리로 업데이트)
            const updateData = {
              updatedAt: serverTimestamp(),
              lastCategory: category || 'general', // 현재 사용된 카테고리
              lastCategoryName: CATEGORY_NAME_MAP[category] || CATEGORY_NAME_MAP['general'] // 현재 사용된 카테고리명
            };
            
            // backend runtime이 아니라 사용자가 선택한 UI 슬롯을 저장한다.
            // 고급 모드의 medium thinking을 재접속 후에도 보존하기 위함이다.
            updateData.selectedModel = conversationModel;
            
            saveStage = 'update_conversation';
            await updateDoc(doc(db, 'conversations', activeConversationId), updateData);
            saveStage = null;
            
            // 🆕 로컬 conversations 배열도 즉시 업데이트 (Firestore 리스너보다 빠르게)
            setConversations(prevConvs => 
              prevConvs.map(conv => 
                conv.id === activeConversationId 
                  ? { 
                      ...conv, 
                      lastCategory: category || 'general',
                      lastCategoryName: CATEGORY_NAME_MAP[category] || CATEGORY_NAME_MAP['general'],
                      selectedModel: conversationModel
                    }
                  : conv
              )
            );
            
            // 최종 메시지 상태 업데이트 (이제 이 부분은 사실상 불필요하지만, UI 즉각 반응을 위해 유지)
            const updatedMessage = { 
              ...aiMessage,
              id: aiMessageRef?.id || aiMessage.id,
              content: _stripSystemTags(completeData.fullResponse || '').trim(), 
              modelId: effectiveModel,
              requestedModelId: selectedModel || null,
              reasoningEffort: completeData.reasoning_effort || null,
              streaming: false,
              isSearching: false,
              web_search_queries: completeData.web_search_queries || [],
              web_search_sources: completeData.web_search_sources || [],
              reasoning_steps: completeData.reasoning_steps || [],
              memory_update: completeData.memory_update || null,
              suggestions: completeSuggestions,
              suggestion_results: {},
              personalization_used: completePersonalizationUsed,
              library_references: completeLibraryReferences,
              past_chat_references: completePastChatReferences,
              artifacts: Array.isArray(completeData.artifacts) ? completeData.artifacts : [],
              promise_without_file: completePromiseWithoutFile,
              edit_not_applied: completeEditNotApplied,
              edit_not_applied_target: completeEditTarget,
              action_not_taken: completeActionNotTaken,
              clarify_card: completeClarifyCard || null,
              clarify_result: null,
              clarify_assumptions: streamedClarifyAssumptions,
              applied_skill: completeAppliedSkill,
              web_images: completeWebImages,
              rich_blocks: completeRichBlocks,
              followup_form: completeFollowupForm,
              followup_form_result: null,
              next_chips: completeNextChips
            };
            
            // 최종 메시지 상태 업데이트 완료
            
            setMessages(prevMessages => 
              prevMessages.map(msg => 
                msg.id === aiMessage.id ? updatedMessage : msg
              )
            );
            
            setLoading(false);
            setAbortController(null);
            setIsStreaming(false);
            linkSpeechKey(aiMessage.id, aiMessageRef?.id);
            
            if (onStreamComplete) {
              onStreamComplete({
                ...completeData,
                conversationId: activeConversationId,
                sessionId,
                aiMessageId: aiMessage.id,
                // Firestore 문서 id — 제안 카드 응답(resolveSuggestion)은 이 id 로 기록해야 한다
                aiMessageDocId: aiMessageRef?.id || aiMessage.id,
                isNewConversation
              });
            }
          } catch (error) {
            console.error('❌ onComplete 콜백 내부에서 DB 저장 실패:', error);
            if (saveStage) _withSaveStage(error, saveStage);
            trackChatUxEvent('save_failed', {
              category: category || 'general',
              errorCode: getTrackingErrorCode(error),
              isAnonymous: false,
              isPro: subscriptionStatus?.isPro === true,
              model: selectedModel,
              stage: saveStage || 'unknown',
            });

            // 다른 대화로 옮겼으면 보존 표시를 하지 않아야 그 대화의 onSnapshot 목록이 화면을 덮는다.
            const saveFailedIn = currentConversationRef.current === activeConversationId
              ? activeConversationId
              : null;
            const receivedContent = _stripSystemTags(completeData?.fullResponse || '').trim();
            setMessages(prevMessages => prevMessages.map(msg => {
              if (msg.id === aiMessage.id) {
                return {
                  ...msg,
                  ...(receivedContent ? { content: receivedContent } : {}),
                  streaming: false,
                  isSearching: false,
                  currentStatus: null,
                  saveFailedIn,
                };
              }
              if (msg.id === userMessage.id) {
                return { ...msg, saveFailedIn };
              }
              return msg;
            }));
            saveFailedNoticeConversationRef.current = saveFailedIn;
            setError(CHAT_SAVE_FAILED_NOTICE);
            setLoading(false);
            setAbortController(null);
            setIsStreaming(false);
            
            if (onStreamError) {
              onStreamError(error);
            }
          }
        },
        // onError
        (error) => {
          contentBatcher.flush();
          console.error('❌ 스트리밍 오류:', error);

          const errorInfo = error.errorInfo || { code: 'unknown', retryable: false, serverMessage: error.message };
          const userMessage = error.message || '문제가 발생했어요. 계속되면 새 대화를 시작해보세요.';
          recordTurnError(activeConversationId, {
            code: errorInfo.code || getTrackingErrorCode(error),
            retryable: errorInfo.retryable,
            model: selectedModel,
            category,
          });
          // 받은 글이 있으면 오류 문구로 덮지 않는다 — 글은 두고 오류는 그 아래 작은 안내로 보인다.
          const receivedPartial = isChatKeepPartialEnabled()
            ? _stripSystemTags(error.partialResponse || '').trim()
            : '';

          if (replace) {
            // 오류 턴은 저장하지 않는다 — 화면도 Firestore 에 남은 옛 턴으로 돌린다.
            restoreOldTurn();
            setError(turnReplaceFailedNotice(replace.action));
            setLoading(false);
            setAbortController(null);
            setIsStreaming(false);
            if (onStreamError) onStreamError(error);
            return;
          }

          setMessages(prevMessages => 
            prevMessages.map(msg => 
              msg.id === aiMessage.id 
                ? (receivedPartial
                    ? {
                        ...msg,
                        content: receivedPartial,
                        streaming: false,
                        isSearching: false,
                        currentStatus: null,
                        error: true,
                        errorInfo,
                        errorNote: userMessage,
                        stopped: 'error',
                        partial: true,
                      }
                    : { ...msg, content: userMessage, streaming: false, error: true, errorInfo })
                : msg
            )
          );
          
          if (!receivedPartial) {
            setError(errorInfo.retryable ? '일시적인 문제가 발생했어요. 다시 시도해주세요.' : '메시지 전송에 문제가 발생했어요.');
          }
          setLoading(false);
          setAbortController(null);
          setIsStreaming(false);
          
          if (onStreamError) {
            onStreamError(error);
          }
        },
        controller.signal,
        // 🔗 Responses API 체인 복구 및 오박사 상담 이전 대화 기록 로드용
        currentUser?.uid || null,
        activeConversationId || null,  // 🆕 모든 대화에서 conversation_id 전달 (체인 복구용)
        // 🆕 PRO AI 모델 선택
        selectedModel,
        // 🆕 PRO 사용자 컨텍스트 확장 (80개 메시지)
        subscriptionStatus?.isPro || false,
        // 🗂️ 첨부 파일 컨텍스트 포함 (PRO 전용)  
        includeAttachedFiles,
        // 🗂️ 특정 파일 ID 목록 전달
        attachedFileIds,
        // 🆕 현재 활성 outline (PPT/학습지) — 채팅 AI가 "N번 슬라이드"의 실체를 정확히 인식
        existingOutline,
        // 첫 턴에서만 백엔드가 저비용 제목을 생성해 Firestore에 저장
        isNewConversation,
        // "PDF로 만들어줘" 같은 후속 요청은 직전 파일 원본을 변환한다.
        sourceArtifactId,
        workspaceTargetFileId,
        // 학습지/PPT 산출물 현황 요약
        artifactState,
        // 📎 이번 턴에 새로 첨부한 파일 ID — 서버가 모델에 "새 첨부"로 표시
        newlyAttachedFileIds,
        // 🧩 P5 자동 스킬 컨텍스트 { activeSkill, skillOverride }
        skillContext,
        activeConversationId || null,
        null,
        isChatKeepPartialEnabled(),
        replace ? { action: replace.action, replaceDocIds: replace.replaceDocIds } : null
      );
      
    } catch (error) {
      console.error('Firebase 스트리밍 메시지 전송 오류:', error);
      
      const errorInfo = error.errorInfo || { code: 'unknown', retryable: false, serverMessage: error.message };
      const userMessage_err = error.message || '문제가 발생했어요. 다시 시도하거나, 계속되면 새 대화를 시작해보세요.';
      recordTurnError(conversationId, {
        code: errorInfo.code || getTrackingErrorCode(error),
        retryable: errorInfo.retryable,
        model: selectedModel,
        category,
      });

      if (replace) {
        restoreOldTurn();
        setError(turnReplaceFailedNotice(replace.action));
        setLoading(false);
        setAbortController(null);
        setIsStreaming(false);
        if (onStreamError) onStreamError(error);
        throw error;
      }

      setMessages(prevMessages => {
        const aiExists = prevMessages.some(msg => msg.id === aiMessage.id);
        if (aiExists) {
          return prevMessages.map(msg =>
            msg.id === aiMessage.id
              ? { ...msg, content: userMessage_err, streaming: false, error: true, errorInfo }
              : msg
          );
        }
        return [...prevMessages, { ...aiMessage, content: userMessage_err, streaming: false, error: true, errorInfo }];
      });
      
      setError(errorInfo.retryable ? '일시적인 문제가 발생했어요. 다시 시도해주세요.' : '메시지 전송에 문제가 발생했어요.');
      setLoading(false);
      setAbortController(null);
      setIsStreaming(false);
      
      if (onStreamError) {
        onStreamError(error);
      }
      
      throw error;
    }
  };

  // 🚀 익명 사용자 스트리밍 메시지 전송
  const sendAnonymousMessageStream = async (content, conversationId = null, onStreamChunk, onStreamComplete, onStreamError, category = 'general', analysisContext = null, selectedModel = null, includeAttachedFiles = false, attachedFileIds = null, existingOutline = null, artifactState = null, turnReplace = null) => {
    const replace = turnReplace && conversationId ? turnReplace : null;
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: content,
      timestamp: new Date()
    };
    
    const aiMessageId = (Date.now() + 1).toString();
    const aiMessage = {
      id: aiMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      streaming: true,
      category: category,
      modelId: selectedModel || 'gpt-5.4-nano',
      isSearching: true,
      currentStatus: null,
      web_search_sources: [],
      artifacts: [],
    };
    
    try {
      setLoading(true);
      setError(null);
      setIsStreaming(true);
      
      // 즉시 화면에 표시 (Optimistic Update)
      //   바꿔 보내기는 옛 턴을 뺀 목록이 이번 턴의 앞 기록이다(서버로 보내는 익명 기록도 같다).
      const messagesBeforeSend = messages;
      const currentMessages = replace ? withoutReplacedTurn(messages, replace) : messages;
      const messagesWithPlaceholder = [...currentMessages, userMessage, aiMessage];
      setMessages(messagesWithPlaceholder);

      const anonymousHistory = buildAnonymousChatHistory(currentMessages);
      if (anonymousHistory.newlyMaskedCount > 0 && onStreamChunk) {
        onStreamChunk({ type: 'pii_masked', count: anonymousHistory.newlyMaskedCount });
      }
      
      let activeConversationId = conversationId;
      let sessionId = conversationId ? sessionMap[conversationId] : null;
      let isNewConversation = false;
      
      if (!activeConversationId) {
        isNewConversation = true;
        activeConversationId = `temp_anonymous_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        
        const sessionData = await initChatSession();
        sessionId = sessionData.session_id;
        
        setSessionMap(prevMap => ({
          ...prevMap,
          [activeConversationId]: sessionId
        }));
      } else if (!sessionId) {
        console.warn('⚠️ 기존 대화에 세션 ID가 없음. 대화 목록에서 찾아보기 시도...');
        
        const conversation = conversations.find(conv => conv.id === activeConversationId);
        if (conversation && conversation.sessionId) {
          sessionId = conversation.sessionId;
          
          setSessionMap(prevMap => ({
            ...prevMap,
            [activeConversationId]: sessionId
          }));
        } else {
          console.warn('⚠️ 세션 ID를 찾을 수 없음. 새 세션 생성...');
          try {
            const sessionData = await initChatSession();
            sessionId = sessionData.session_id;
            
            setSessionMap(prevMap => ({
              ...prevMap,
              [activeConversationId]: sessionId
            }));
            
            const updatedConversations = conversations.map(conv => 
              conv.id === activeConversationId ? { ...conv, sessionId } : conv
            );
            setConversations(updatedConversations);
            saveAnonymousConversations(updatedConversations);
          } catch (err) {
            console.error('❌ 새 세션 생성 실패:', err);
            throw new Error('세션을 생성할 수 없습니다. 새로고침 후 다시 시도해주세요.');
          }
        }
      }
      
      // 바꿔 보내기는 답이 끝나기 전까지 옛 턴을 저장소에 둔다 — 도중에 새로고침·탭 닫기를 해도 옛 턴이 남게.
      saveAnonymousMessages(activeConversationId, replace ? messagesBeforeSend : messagesWithPlaceholder);
      
      // AbortController 생성
      const controller = new AbortController();
      setAbortController(controller);
      
      // 익명 사용자 메시지 카운트 증가
      incrementAnonymousMessageCount();
      // 🃏 이 턴의 사진 카드 — complete 의 rich_blocks 가 최종본
      let anonymousRichBlocks = [];
      // 🖼️ 이 턴의 사진 줄 — complete 의 web_images 가 최종본
      let anonymousWebImages = [];
      
      // ✍️ 본문 청크는 화면 프레임마다 마지막 값만 반영한다
      const contentBatcher = createLatestFrameBatcher((cleanContent) => {
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            msg.id === aiMessageId
              ? {
                  ...msg,
                  content: cleanContent,
                  currentStatus: null,
                  streaming: true,
                  isSearching: false,
                  ...(detectPendingArtifactFromContent(msg, cleanContent) || {})
                }
              : msg
          )
        );
      });
      
      // 스트리밍 응답 처리 (카테고리 정보 및 분석 컨텍스트 포함)
      await getAIResponseStream(
        content,
        sessionId,
        category,
        analysisContext,
        // onChunk
        (chunkData) => {
          if (chunkData.type !== 'content') contentBatcher.flush();
          // 🎯 첫 번째 응답이 오기 시작할 때까지 로딩 유지
          if (chunkData.type === 'start') {
            //console.log('🚀 AI 응답 스트리밍 시작됨 (익명 사용자)');
            // 세션 ID 업데이트
            if (chunkData.sessionId && chunkData.sessionId !== sessionId) {
              sessionId = chunkData.sessionId;
              setSessionMap(prevMap => ({
                ...prevMap,
                [activeConversationId]: sessionId
              }));
            }
            // 로딩은 계속 유지 (아직 setLoading(false) 하지 않음)
          } else if (isArtifactProgressChunk(chunkData)) {
            // 📎 파일 생성 진행 카드 (익명 사용자는 서버가 auth_required 실패를 보내 카드가 내려간다)
            setMessages(prevMessages =>
              prevMessages.map(msg =>
                msg.id === aiMessageId
                  ? { ...msg, ...applyArtifactChunk(msg, chunkData) }
                  : msg
              )
            );
          } else if (chunkData.type === 'status' && !chunkData.quiet) {
            // 🆕 AI 상태 업데이트 (thinking, web search 등). quiet 안내는 칩 라벨만 바꾼다(ChatPage).
            setMessages(prevMessages => 
              prevMessages.map(msg => 
                msg.id === aiMessageId 
                  ? { 
                      ...msg, 
                      currentStatus: {
                        type: chunkData.statusType,
                        message: chunkData.message,
                        icon: chunkData.icon,
                        detail: chunkData.detail
                      },
                      ...(chunkData.steps?.length
                        ? { activitySteps: appendActivitySteps(msg.activitySteps, chunkData.steps) }
                        : {}),
                      streaming: true,
                      isSearching: true
                    }
                  : msg
              )
            );
          } else if (chunkData.type === 'content') {
            contentBatcher.schedule(_stripSystemTags(chunkData.fullContent || chunkData.chunk || ''));
          } else if (chunkData.type === 'rich_block' && Number.isInteger(chunkData.index)) {
            anonymousRichBlocks = mergeRichBlock(anonymousRichBlocks, chunkData);
            const nextBlocks = anonymousRichBlocks;
            setMessages(prevMessages =>
              prevMessages.map(msg => (msg.id === aiMessageId ? { ...msg, rich_blocks: nextBlocks } : msg))
            );
          } else if (chunkData.type === 'image_strip' && Number.isInteger(chunkData.index)) {
            anonymousWebImages = mergeWebImageStrip(anonymousWebImages, chunkData);
            const nextStrips = anonymousWebImages;
            setMessages(prevMessages =>
              prevMessages.map(msg => (msg.id === aiMessageId ? { ...msg, web_images: nextStrips } : msg))
            );
          }
          
          // 외부 콜백 호출
          if (onStreamChunk) {
            onStreamChunk(chunkData);
          }
        },
        // onComplete
        (completeData) => {
          contentBatcher.flush();
          let finalConversationId = activeConversationId;
          // chatApi 는 이 콜백의 동기 예외를 JSON 파싱 오류로 삼키므로 localStorage 예외(용량 초과 등)가 새면 로딩이 풀리지 않는다.
          let localSaveError = null;
          const saveLocally = (write) => {
            try {
              write();
            } catch (saveError) {
              localSaveError = localSaveError || saveError;
            }
          };
          const reportLocalSaveFailure = () => {
            if (!localSaveError) return;
            console.warn('익명 대화 기록을 저장하지 못했어요:', localSaveError);
            trackChatUxEvent('save_failed', {
              category: category || 'general',
              errorCode: getTrackingErrorCode(localSaveError),
              isAnonymous: true,
              model: selectedModel,
              stage: 'save_local',
            });
            setMessages(prevMessages => prevMessages.map(msg => (
              msg.id === aiMessageId || msg.id === userMessage.id ? { ...msg, saveFailedIn: finalConversationId } : msg
            )));
            saveFailedNoticeConversationRef.current = finalConversationId;
            setError(CHAT_SAVE_FAILED_NOTICE);
          };

          if (completeData.aborted) {
            setMessages(messagesBeforeSend);
            if (isNewConversation) {
              localStorage.removeItem(
                `${ANONYMOUS_MESSAGES_PREFIX}${activeConversationId}`
              );
              setSessionMap((previousMap) => {
                const nextMap = { ...previousMap };
                delete nextMap[activeConversationId];
                return nextMap;
              });
              setCurrentConversation(null);
              finalConversationId = null;
            } else {
              saveLocally(() => saveAnonymousMessages(activeConversationId, messagesBeforeSend));
            }

            reportLocalSaveFailure();
            setLoading(false);
            setAbortController(null);
            setIsStreaming(false);

            if (onStreamComplete) {
              onStreamComplete({
                ...completeData,
                conversationId: finalConversationId,
                sessionId,
                aiMessageId,
                isNewConversation,
              });
            }
            return;
          }
          
          // 🎯 [신규] 새 대화였을 경우, 여기서 실제 대화 정보를 생성하고 저장합니다.
          if (isNewConversation) {
            finalConversationId = `anonymous_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            
            const newConversation = {
              id: finalConversationId,
              title: content.substring(0, 30) + (content.length > 30 ? '...' : ''),
              date: new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', 
                month: 'long', 
                day: 'numeric'
              }),
              updatedAt: new Date(),
              sessionId: sessionId,
              lastCategory: category || 'general', // 현재 선택된 카테고리
              lastCategoryName: CATEGORY_NAME_MAP[category] || CATEGORY_NAME_MAP['general'] // 현재 선택된 카테고리명
            };

            const updatedConversations = [newConversation, ...conversations];
            setConversations(updatedConversations);
            saveLocally(() => saveAnonymousConversations(updatedConversations));

            // 세션 맵을 임시 ID에서 실제 ID로 교체합니다.
            setSessionMap(prevMap => {
              const newMap = { ...prevMap };
              delete newMap[activeConversationId];
              newMap[finalConversationId] = sessionId;
              return newMap;
            });
            
            // 현재 대화 ID도 실제 ID로 업데이트합니다.
            saveLocally(() => setCurrentConversation(finalConversationId));
          }
          
          // 최종 메시지 업데이트
          const finalAiMessage = {
            id: aiMessageId,
            role: 'assistant',
            content: _stripSystemTags(completeData.fullResponse || '').trim(),
            rich_blocks: finalizeRichBlocks(completeData.rich_blocks, anonymousRichBlocks),
            web_images: finalizeWebImages(completeData.web_images, anonymousWebImages),
            followup_form: completeData.followup_form || null,
            next_chips: Array.isArray(completeData.next_chips) ? completeData.next_chips : [],
            modelId: normalizeChatModelId(
              completeData.effective_model || selectedModel || 'gpt-5.4-nano'
            ),
            requestedModelId: selectedModel || null,
            reasoningEffort: completeData.reasoning_effort || null,
            timestamp: new Date(),
            streaming: false,
            isSearching: false,
            web_search_queries: completeData.web_search_queries || [],
            web_search_sources: completeData.web_search_sources || [],
            reasoning_steps: completeData.reasoning_steps || [],
            memory_update: completeData.memory_update || null,
            suggestions: _normalizeSuggestions(completeData.suggestions),
            suggestion_results: {},
            personalization_used: _normalizePersonalizationUsed(completeData.personalization_used),
            artifacts: Array.isArray(completeData.artifacts) ? completeData.artifacts : []
          };
          
          // 🆕 Responses API 체인 정보 저장 (익명 사용자도 동일)
          if (completeData.response_id) {
            finalAiMessage.response_id = completeData.response_id;
            finalAiMessage.response_chain_len = completeData.response_chain_len || 0;
          }
          
          setMessages(prevMessages => {
            const updatedMessages = prevMessages.map(msg => 
              msg.id === aiMessageId ? finalAiMessage : msg
            );
            
            // 로컬 스토리지에 저장 (실제 대화 ID로)
            saveLocally(() => saveAnonymousMessages(finalConversationId, updatedMessages));
            return updatedMessages;
          });
          
          // 🔧 대화 목록 업데이트 - 최신 순으로 정렬 (새 대화든 기존 대화든 카테고리 정보 업데이트)
          if (!isNewConversation) {
            const now = new Date();
            const updatedConversations = conversations.map(conv => 
              conv.id === finalConversationId // ❗ activeConversationId를 finalConversationId로 변경
                ? { 
                    ...conv, 
                    date: now.toLocaleDateString('ko-KR', {
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric'
                    }),
                    updatedAt: now, // 정렬용 타임스탬프 추가
                    lastCategory: category || 'general', // 현재 사용된 카테고리
                    lastCategoryName: CATEGORY_NAME_MAP[category] || CATEGORY_NAME_MAP['general'] // 현재 사용된 카테고리명
                  }
                : conv
            ).sort((a, b) => {
              // updatedAt이 있으면 그것으로, 없으면 date로 정렬
              const dateA = a.updatedAt || new Date(a.date);
              const dateB = b.updatedAt || new Date(b.date);
              return dateB - dateA; // 최신 순
            });
            
            setConversations(updatedConversations);
            saveLocally(() => saveAnonymousConversations(updatedConversations));
          }
          
          reportLocalSaveFailure();
          // 로딩 해제
          setLoading(false);
          setAbortController(null);
          setIsStreaming(false);
          
          if (onStreamComplete) {
            onStreamComplete({
              ...completeData,
              conversationId: finalConversationId,
              sessionId,
              aiMessageId: aiMessageId,
              isNewConversation
            });
          }
        },
        // onError
        (error) => {
          contentBatcher.flush();
          console.error('❌ 스트리밍 메시지 오류:', error);

          const errorInfo = error.errorInfo || { code: 'unknown', retryable: false, serverMessage: error.message };
          const userMessage = error.message || '문제가 발생했어요. 계속되면 새 대화를 시작해보세요.';

          if (replace) {
            setMessages(messagesBeforeSend);
            try {
              saveAnonymousMessages(activeConversationId, messagesBeforeSend);
            } catch (_) {
              // 로컬 저장 실패는 다음 저장에서 다시 쓴다.
            }
            setError(turnReplaceFailedNotice(replace.action));
            setLoading(false);
            setAbortController(null);
            setIsStreaming(false);
            if (onStreamError) onStreamError(error);
            return;
          }

          setMessages(prevMessages => 
            prevMessages.map(msg => 
              msg.id === aiMessageId 
                ? { ...msg, content: userMessage, streaming: false, error: true, errorInfo }
                : msg
            )
          );
          
          setError(errorInfo.retryable ? '일시적인 문제가 발생했어요. 다시 시도해주세요.' : '메시지 전송에 문제가 발생했어요.');
          setLoading(false);
          setAbortController(null);
          setIsStreaming(false);
          
          if (onStreamError) {
            onStreamError(error);
          }
        },
        // signal
        controller.signal,
        // 🩺 오박사 상담 이전 대화 기록 로드용 (익명 사용자용)
        category === 'oh-doctor' ? userDetails?.uid : null,
        category === 'oh-doctor' ? activeConversationId : null,
        // 🆕 PRO AI 모델 선택
        selectedModel,
        // 🆕 익명 사용자는 항상 FREE (40개 메시지 컨텍스트)
        false,
        // 🗂️ 첨부 파일 컨텍스트 포함 (익명 사용자는 파일 첨부 불가)
        false,
        // 🗂️ 익명 사용자는 파일 ID 전달 불가
        null,
        // 🆕 현재 활성 outline (익명도 동일하게 전달)
        existingOutline,
        // 익명은 제목 생성/파일 변환/작업 대상 파일이 없다
        false,
        null,
        null,
        // 학습지/PPT 산출물 현황 요약
        artifactState,
        null,
        null,
        activeConversationId || null,
        // 익명 대화는 서버 워커 메모리에만 있어 다른 워커가 받으면 앞 대화를 모른다 → 최근 기록을 함께 보낸다
        anonymousHistory,
        false,
        replace ? { action: replace.action } : null
      );
      
    } catch (error) {
      console.error('스트리밍 메시지 전송 오류:', error);
      
      const errorInfo = error.errorInfo || { code: 'unknown', retryable: false, serverMessage: error.message };
      const userMessage_err = error.message || '문제가 발생했어요. 다시 시도하거나, 계속되면 새 대화를 시작해보세요.';

      if (replace) {
        setMessages(messages);
        setError(turnReplaceFailedNotice(replace.action));
        setLoading(false);
        setAbortController(null);
        setIsStreaming(false);
        if (onStreamError) onStreamError(error);
        throw error;
      }

      setMessages(prevMessages => {
        const aiExists = prevMessages.some(msg => msg.id === aiMessageId);
        if (aiExists) {
          return prevMessages.map(msg =>
            msg.id === aiMessageId
              ? { ...msg, content: userMessage_err, streaming: false, error: true, errorInfo }
              : msg
          );
        }
        return [...prevMessages, { ...aiMessage, content: userMessage_err, streaming: false, error: true, errorInfo }];
      });
      
      setError(errorInfo.retryable ? '일시적인 문제가 발생했어요. 다시 시도해주세요.' : '메시지 전송에 문제가 발생했어요.');
      setLoading(false);
      setAbortController(null);
      setIsStreaming(false);
      
      if (onStreamError) {
        onStreamError(error);
      }
      
      throw error;
    }
  };
  
  // 익명 사용자의 대화 삭제
  const deleteAnonymousConversation = async (conversationId) => {
    try {
      setLoading(true);
      
      // 대화 목록에서 제거
      const updatedConversations = conversations.filter(conv => conv.id !== conversationId);
      setConversations(updatedConversations);
      saveAnonymousConversations(updatedConversations);
      
      // 메시지 삭제
      localStorage.removeItem(`${ANONYMOUS_MESSAGES_PREFIX}${conversationId}`);
      
      // 세션 매핑에서 제거
      setSessionMap(prevMap => {
        const newMap = { ...prevMap };
        delete newMap[conversationId];
        return newMap;
      });
      
      // 현재 선택된 대화가 삭제되는 경우 선택 해제
      if (currentConversation === conversationId) {
        setCurrentConversation(null);
        localStorage.removeItem(ANONYMOUS_CURRENT_CONVERSATION_KEY);
      }
      
      return { success: true };
    } catch (err) {
      console.error('익명 대화 삭제 오류:', err);
      setError('대화를 삭제하는 중 오류가 발생했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // 대화 삭제 기능
  const deleteConversation = async (conversationId) => {
    // 익명 사용자인 경우
    if (isAnonymous && userDetails) {
      return deleteAnonymousConversation(conversationId);
    }
    
    // Firebase 인증된 사용자인 경우
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }
    
    try {
      setLoading(true);
      
      // 대화 소유자 확인
      const conversationDoc = await getDoc(doc(db, 'conversations', conversationId));
      if (!conversationDoc.exists() || conversationDoc.data().userId !== currentUser.uid) {
        throw new Error('삭제 권한이 없습니다.');
      }
      
      // 메시지 하위 컬렉션 삭제
      const messagesSnapshot = await getDocs(collection(db, 'conversations', conversationId, 'messages'));
      
      // 배치 작업으로 문서 삭제
      const batch = writeBatch(db);
      
      // 메시지 문서 삭제
      messagesSnapshot.docs.forEach(doc => batch.delete(doc.ref));
      
      // 대화 문서 삭제
      batch.delete(doc(db, 'conversations', conversationId));
      
      // 배치 실행
      await batch.commit();

      const attachmentRefs = collectConversationAttachmentRefs(
        conversationDoc.data(),
        messagesSnapshot.docs.map((messageDoc) => messageDoc.data()),
        [sessionMap[conversationId]],
      );
      // 첨부가 없어도 부른다 — 지난 대화 찾기용 글도 함께 지운다.
      cleanupConversationAttachments(conversationId, attachmentRefs).catch((cleanupError) => {
        console.warn('대화 정리 실패:', cleanupError?.message);
      });
      
      // 세션 매핑에서 제거
      setSessionMap(prevMap => {
        const newMap = { ...prevMap };
        delete newMap[conversationId];
        return newMap;
      });
      
      // 현재 선택된 대화가 삭제되는 경우 선택 해제
      if (currentConversation === conversationId) {
        setCurrentConversation(null);
      }
      
      return { success: true };
    } catch (err) {
      console.error('대화 삭제 오류:', err);
      setError('대화를 삭제하는 중 오류가 발생했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // options.selectedImageModel: 첫 생성에 쓰는 이미지 모델 id → 대화 문서에 함께 저장(다른 기기에서 복원)
  // options.onSaveError(error, stage, conversationId): 재시도까지 실패했을 때 호출 — 호출부가 안내·계측한다.
  //   이미 돌려준 id 의 저장이 늦게 실패하면 그 id 를 함께 넘긴다(호출부가 버리고 다음 턴에 새로 만든다).
  const createImageConversation = useCallback(async (title, options = {}) => {
    if (!currentUser) return null;
    try {
      const conversationData = {
        userId: currentUser.uid,
        title: (title || '이미지 생성').substring(0, 30) + (title && title.length > 30 ? '...' : ''),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sessionId: null,
        lastCategory: 'image_generation',
        lastCategoryName: '이미지 생성',
      };
      const selectedImageModel = options && typeof options.selectedImageModel === 'string'
        ? options.selectedImageModel.trim()
        : '';
      if (selectedImageModel) {
        conversationData.selectedImageModel = selectedImageModel;
        conversationData.imageModelExplicit = true;
      }
      // Firestore SDK 는 네트워크가 끊기면 쓰기를 실패시키지 않고 대기열에 둔 채 기다린다.
      // id 는 이미 정해졌으니 확인이 늦으면 그 id 로 진행하고(이후 메시지 쓰기도 같은 순서로 대기),
      // 늦게 최종 실패하면 그때 onSaveError 로 알린다.
      const ref = doc(collection(db, 'conversations'));
      const write = _setDocWithRetry(ref, conversationData, 1);
      const settled = await Promise.race([
        write.then(() => 'saved'),
        new Promise((resolve) => setTimeout(() => resolve('pending'), IMAGE_CONVERSATION_CREATE_WAIT_MS)),
      ]);
      if (settled === 'pending') {
        write.catch((error) => {
          console.error('이미지 대화 생성 실패(지연):', error);
          options?.onSaveError?.(error, 'create_conversation', ref.id);
        });
      }
      return ref.id;
    } catch (error) {
      console.error('이미지 대화 생성 실패:', error);
      options?.onSaveError?.(error, 'create_conversation');
      return null;
    }
  }, [currentUser]);

  // 저장되면 true. 메시지 저장이 재시도까지 실패하면 false 와 함께 options.onSaveError(error, stage) 를 부른다.
  // updatedAt 갱신 실패는 목록 정렬에만 영향이 있어 계측만 하고 true 를 돌려준다.
  const addImageMessage = useCallback(async (conversationId, content, role, metadata = {}, options = {}) => {
    if (!currentUser || !conversationId) return false;
    const stage = role === 'user' ? 'save_user' : 'save_ai';
    try {
      await _createMessageDoc(conversationId, {
        content: content || '',
        role,
        timestamp: serverTimestamp(),
        category: 'image_generation',
        ...metadata,
      }, { retries: 1 });
    } catch (error) {
      console.error('이미지 메시지 저장 실패:', error);
      options?.onSaveError?.(error, stage);
      return false;
    }
    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('이미지 대화 갱신 실패:', error);
      options?.onSaveError?.(error, 'update_conversation');
    }
    return true;
  }, [currentUser]);

  const updateImageConversationSession = useCallback(async (conversationId, sessionId) => {
    if (!conversationId || !sessionId) return { success: false, error: 'missing-session' };
    if (!currentUser) return { success: false, error: 'no-user' };

    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        sessionId,
        lastCategory: 'image_generation',
        lastCategoryName: '이미지 생성',
        updatedAt: serverTimestamp(),
      });

      setSessionMap(prevMap => ({
        ...prevMap,
        [conversationId]: sessionId,
      }));
      setConversations(prevConvs =>
        prevConvs.map(conv =>
          conv.id === conversationId
            ? {
                ...conv,
                sessionId,
                lastCategory: 'image_generation',
                lastCategoryName: '이미지 생성',
              }
            : conv
        )
      );

      return { success: true };
    } catch (error) {
      console.error('이미지 세션 저장 실패:', error);
      return { success: false, error };
    }
  }, [currentUser]);

  // 이미지 채팅: 사용자가 헤더에서 모델을 바꾸면 대화 문서 selectedImageModel 에 반영 (호출부가 디바운스·실패 무시)
  const updateImageConversationModel = useCallback(async (conversationId, modelId) => {
    const normalizedModelId = typeof modelId === 'string' ? modelId.trim() : '';
    if (!conversationId || !normalizedModelId) return { success: false, error: 'missing-model' };
    if (!currentUser) return { success: false, error: 'no-user' };

    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        selectedImageModel: normalizedModelId,
        imageModelExplicit: true,
      });
      setConversations(prevConvs =>
        prevConvs.map(conv =>
          conv.id === conversationId
            ? { ...conv, selectedImageModel: normalizedModelId, imageModelExplicit: true }
            : conv
        )
      );
      return { success: true };
    } catch (error) {
      console.warn('이미지 모델 선택 저장 실패(무시):', error?.code || error?.message || error);
      return { success: false, error };
    }
  }, [currentUser]);

  // 이미지 채팅: 사용자가 직접 고른 모델만. 플래그 없는 옛 저장값·메시지 메타는 선택으로 승격하지 않는다.
  const getImageConversationModel = useCallback((conversationId) => {
    if (!conversationId) return null;
    const conversation = conversations.find((conv) => conv.id === conversationId);
    if (
      conversation
      && conversation.imageModelExplicit === true
      && typeof conversation.selectedImageModel === 'string'
      && conversation.selectedImageModel
    ) {
      return conversation.selectedImageModel;
    }
    return null;
  }, [conversations]);

  const clearErrorMessages = useCallback((errorMessageId = null) => {
    setMessages(prev => removeFailedTurn(prev, errorMessageId));
    setError(null);
  }, []);

  // [B5] AI 응답에서 시스템 태그 제거 — 사용자에게 노출되지 않게.
  // 지원 태그: [AUTO_GENERATE], [STYLE_UPDATE:...], [SLIDE_EDIT:N:...], [ITEM_EDIT:N:...]
  const _stripSystemTags = stripChatSystemTags;

  // Outline 직렬화/역직렬화.
  // Firestore는 배열 바로 안에 배열을 저장할 수 없다 (예: rows: [[...], [...]]).
  // 활동지(worksheet_type=activity)의 sections[].content_items[].rows는 2D 배열을 포함하기 때문에
  // object 그대로 updateDoc하면 400 오류로 "확정적" 저장 실패가 발생한다.
  // 해결: outline 전체를 JSON 문자열로 저장. 로드 시 string이면 parse, object면 그대로 사용(하위 호환).
  // Stage H: outline 저장 시 canvas envelope 자동 stamp
  // - slide.canvas가 있고 format_version이 지원 버전(v2/v3)이 아니면 현재 버전(v3) 부여
  // - 이미 v2/v3인 문서는 그대로 둔다 (v3 → v2로 되돌리지 않는다; canvasSchema.js 참조)
  // - v1 자료는 canvas 없으므로 stamping 안 됨 (기존 호환 유지)
  const _stampFormatVersionIfCanvas = (outline) => {
    if (!outline || typeof outline !== 'object') return outline;
    const slides = outline.slides;
    if (!Array.isArray(slides)) return outline;
    const hasCanvas = slides.some(
      (s) => s && typeof s === 'object' && s.canvas && Array.isArray(s.canvas.objects),
    );
    if (hasCanvas && !SUPPORTED_CANVAS_FORMAT_VERSIONS.has(outline.format_version)) {
      return { ...outline, format_version: CANVAS_FORMAT_VERSION };
    }
    return outline;
  };
  const _serializeOutline = (outline) => {
    if (outline == null) return outline;
    const stamped = _stampFormatVersionIfCanvas(outline);
    try { return JSON.stringify(stamped); }
    catch (e) { console.warn('outline 직렬화 실패, 원본으로 저장 시도:', e); return stamped; }
  };
  // 일시적 네트워크/권한 오류에 대비해 Firestore updateDoc을 2회까지 재시도한다.
  // fire-and-forget이 아니라 결과({success,error})를 호출자에 명시 반환하여
  // 저장 실패를 UI에서 토스트로 surface할 수 있게 한다.
  const _updateConversationWithRetry = async (conversationId, updateData, label) => {
    const MAX_ATTEMPTS = 2;
    const RETRY_DELAY_MS = 400;
    let lastErr = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        await updateDoc(doc(db, 'conversations', conversationId), updateData);
        return { success: true };
      } catch (err) {
        lastErr = err;
        if (attempt < MAX_ATTEMPTS) {
          console.warn(`${label} 저장 ${attempt}/${MAX_ATTEMPTS} 실패, 재시도:`, err?.code || err?.message || err);
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        }
      }
    }
    console.error(`${label} 저장 최종 실패:`, lastErr);
    return { success: false, error: lastErr };
  };

  // 🧠 제안 카드 응답 기록 — 로컬 상태를 먼저 갱신(낙관적)하고
  //   로그인 사용자는 conversations/{id}/messages/{messageId}.suggestion_results.<id> 에 merge,
  //   비로그인 사용자는 localStorage 메시지 저장소에 반영한다.
  //   멀티워커/새로고침에도 카드 상태가 유지되도록 상태는 메시지 문서가 진실이다.
  const resolveSuggestion = async (
    conversationId,
    messageId,
    suggestionId,
    { result, memory_id = null } = {}
  ) => {
    if (!conversationId || !messageId || !suggestionId || !result) {
      return { success: false, error: 'invalid-args' };
    }
    const entry = {
      result,
      resolved_at: new Date().toISOString(),
      ...(memory_id ? { memory_id } : {}),
    };

    setMessages((prev) => {
      const updated = prev.map((msg) => (
        msg.id === messageId
          ? {
              ...msg,
              suggestion_results: {
                ...(msg.suggestion_results || {}),
                [suggestionId]: entry,
              },
            }
          : msg
      ));
      if (isAnonymous || !currentUser) {
        try {
          saveAnonymousMessages(conversationId, updated);
        } catch (_e) {
          // localStorage 용량 초과 등 — 화면 상태는 이미 갱신됐다.
        }
      }
      return updated;
    });

    if (isAnonymous || !currentUser) {
      return { success: true, persisted: 'local', entry };
    }

    const key = String(suggestionId);
    const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
    try {
      if (FIRESTORE_FIELD_PATH_RESERVED.test(key)) {
        // 점 표기가 불가능한 id 는 FieldPath 로 같은 항목만 갱신한다.
        // ({ suggestion_results: { [key]: entry } } 로 updateDoc 하면 맵 전체가 바뀌어
        //  같은 메시지의 다른 제안 응답이 지워진다)
        await updateDoc(messageRef, new FieldPath('suggestion_results', key), entry);
      } else {
        await updateDoc(messageRef, { [`suggestion_results.${key}`]: entry });
      }
      return { success: true, persisted: 'firestore', entry };
    } catch (err) {
      console.warn('제안 카드 응답 저장 실패:', err?.code || err?.message || err);
      return { success: false, error: err, entry };
    }
  };

  // 🃏 되묻기 카드 응답 기록 — 로컬 먼저(낙관적), 메시지 문서 clarify_result 에 저장.
  //   result: answered | edited | timer_expired | skipped, answers: [{key, label, value}]
  const resolveClarifyCard = async (conversationId, messageId, { result, answers = [] } = {}) => {
    if (!conversationId || !messageId || !result) {
      return { success: false, error: 'invalid-args' };
    }
    const entry = {
      result,
      answers: Array.isArray(answers) ? answers.slice(0, 3) : [],
      resolved_at: new Date().toISOString(),
    };
    setMessages((prev) => prev.map((msg) => (
      msg.id === messageId ? { ...msg, clarify_result: entry } : msg
    )));
    if (isAnonymous || !currentUser) {
      return { success: true, persisted: 'local', entry };
    }
    try {
      await updateDoc(doc(db, 'conversations', conversationId, 'messages', messageId), { clarify_result: entry });
      return { success: true, persisted: 'firestore', entry };
    } catch (err) {
      console.warn('되묻기 카드 응답 저장 실패:', err?.code || err?.message || err);
      return { success: false, error: err, entry };
    }
  };

  // 🧭 조건 고르기 양식 제출 기록 — 로컬 먼저(낙관적), 메시지 문서 followup_form_result 에 저장(새로고침 뒤에도 고른 값 고정).
  const resolveFollowupForm = async (conversationId, messageId, { answers = [] } = {}) => {
    if (!conversationId || !messageId) {
      return { success: false, error: 'invalid-args' };
    }
    const entry = {
      answers: Array.isArray(answers) ? answers.slice(0, 3) : [],
      resolved_at: new Date().toISOString(),
    };
    setMessages((prev) => prev.map((msg) => (
      msg.id === messageId ? { ...msg, followup_form_result: entry } : msg
    )));
    if (isAnonymous || !currentUser) {
      return { success: true, persisted: 'local', entry };
    }
    try {
      await updateDoc(doc(db, 'conversations', conversationId, 'messages', messageId), { followup_form_result: entry });
      return { success: true, persisted: 'firestore', entry };
    } catch (err) {
      console.warn('조건 양식 기록 저장 실패:', err?.code || err?.message || err);
      return { success: false, error: err, entry };
    }
  };

  // 카테고리 이어가기 카드를 이 대화에서 다시 보여주지 않는다.
  // 로그인: conversations/{id}.dismissedHandoffTargets arrayUnion
  // 익명: localStorage 대화 목록에 같은 배열을 남긴다.
  const dismissHandoffTarget = async (conversationId, targetCategory) => {
    if (!conversationId || !HANDOFF_DISMISS_TARGET_SET.has(targetCategory)) {
      return { success: false, error: 'invalid-args' };
    }

    let nextTargets = [targetCategory];
    setConversations((prev) => {
      const updated = prev.map((conv) => {
        if (conv.id !== conversationId) return conv;
        const existing = _normalizeDismissedHandoffTargets(conv.dismissedHandoffTargets);
        nextTargets = existing.includes(targetCategory)
          ? existing
          : [...existing, targetCategory];
        return { ...conv, dismissedHandoffTargets: nextTargets };
      });
      if (isAnonymous || !currentUser) {
        try {
          saveAnonymousConversations(updated);
        } catch (_e) {
          // localStorage 용량 초과 등 — 화면 상태는 이미 갱신됐다.
        }
      }
      return updated;
    });

    if (isAnonymous || !currentUser) {
      return { success: true, persisted: 'local', dismissedHandoffTargets: nextTargets };
    }

    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        dismissedHandoffTargets: arrayUnion(targetCategory),
      });
      return { success: true, persisted: 'firestore', dismissedHandoffTargets: nextTargets };
    } catch (err) {
      console.warn('핸드오프 제안 숨김 저장 실패:', err?.code || err?.message || err);
      return { success: false, error: err };
    }
  };

  // 헤더에서 카테고리(도구)를 바꾼 즉시 대화의 lastCategory 를 갱신한다 (P1 원스톱).
  // 이전엔 다음 메시지 전송이 끝날 때만 저장돼, 바꾼 뒤 메시지를 보내지 않고 나가면
  // 재오픈 시 옛 카테고리 헤더로 열렸다. 산출물 상태는 여기서 건드리지 않는다(카드는 kind 기준).
  const updateConversationCategory = async (conversationId, category) => {
    const normalized = typeof category === 'string' ? category.trim() : '';
    if (!conversationId || !normalized) {
      return { success: false, error: 'invalid-args' };
    }
    const categoryName = CATEGORY_NAME_MAP[normalized] || CATEGORY_NAME_MAP['general'];

    setConversations((prev) => {
      const updated = prev.map((conv) => (
        conv.id === conversationId
          ? { ...conv, lastCategory: normalized, lastCategoryName: categoryName }
          : conv
      ));
      if (isAnonymous || !currentUser) {
        try {
          saveAnonymousConversations(updated);
        } catch (_e) {
          // localStorage 실패는 화면 상태에 영향 없음
        }
      }
      return updated;
    });

    if (isAnonymous || !currentUser) {
      return { success: true, persisted: 'local' };
    }
    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastCategory: normalized,
        lastCategoryName: categoryName,
      });
      return { success: true, persisted: 'firestore' };
    } catch (err) {
      console.warn('카테고리 즉시 저장 실패:', err?.code || err?.message || err);
      return { success: false, error: err };
    }
  };

  // 🖼️ P3: 대화 안에서 만든 이미지를 assistant 메시지로 남긴다 (카테고리 이동 없음).
  //   로그인: Firestore messages 에 저장 → onSnapshot 이 화면에 반영. 익명: 로컬 상태 + localStorage.
  //   이미지 채팅(ImageChatMode)의 addImageMessage 와 달리 lastCategory 를 바꾸지 않는다.
  const addInlineImageMessage = async (conversationId, {
    content = '',
    imageUrl,
    sourcePrompt = '',
    proposalId = null,
    imageModel = null,
  } = {}) => {
    if (!conversationId || !imageUrl) return { success: false, error: 'invalid-args' };
    const base = {
      role: 'assistant',
      content: content || '',
      hasImage: true,
      imageUrl,
      inline_image: {
        source_prompt: String(sourcePrompt || '').slice(0, 2000),
        proposal_id: proposalId,
        image_model: imageModel,
        format: 'png',
      },
      streaming: false,
    };
    if (isAnonymous || !currentUser) {
      const localMessage = { ...base, id: `inline-image-${Date.now()}`, timestamp: new Date() };
      setMessages((prev) => {
        const updated = [...prev, localMessage];
        try {
          saveAnonymousMessages(conversationId, updated);
        } catch (_e) {
          // data URL 이 커서 localStorage 용량을 넘을 수 있다 — 화면에는 남고 재오픈 시 사라질 수 있음
        }
        return updated;
      });
      return { success: true, persisted: 'local', messageId: localMessage.id };
    }
    try {
      const ref = await _createMessageDoc(conversationId, {
        ...base,
        timestamp: serverTimestamp(),
      });
      await updateDoc(doc(db, 'conversations', conversationId), { updatedAt: serverTimestamp() });
      return { success: true, persisted: 'firestore', messageId: ref.id };
    } catch (err) {
      console.warn('인라인 이미지 메시지 저장 실패:', err?.code || err?.message || err);
      return { success: false, error: err };
    }
  };

  // 🖼️ P3: 인라인 이미지 세션 id — 같은 대화에서 "다시 만들기" 가 이전 이미지를 이어 받을 수 있게.
  //   이미지 채팅의 sessionId/lastCategory 는 건드리지 않는다.
  const updateConversationInlineImageSession = async (conversationId, sessionId) => {
    const normalized = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!conversationId || !normalized) return { success: false, error: 'invalid-args' };
    setConversations((prev) => {
      const updated = prev.map((conv) => (
        conv.id === conversationId ? { ...conv, inlineImageSessionId: normalized } : conv
      ));
      if (isAnonymous || !currentUser) {
        try {
          saveAnonymousConversations(updated);
        } catch (_e) {
          // 무시
        }
      }
      return updated;
    });
    if (isAnonymous || !currentUser) return { success: true, persisted: 'local' };
    try {
      await updateDoc(doc(db, 'conversations', conversationId), { inlineImageSessionId: normalized });
      return { success: true, persisted: 'firestore' };
    } catch (err) {
      console.warn('인라인 이미지 세션 저장 실패:', err?.code || err?.message || err);
      return { success: false, error: err };
    }
  };

  // 자료함 저장 제안을 이 대화에서 다시 보여주지 않는다.
  // 로그인: conversations/{id}.dismissedLibrarySuggestionFileIds arrayUnion
  // 익명: localStorage 대화 목록에 같은 배열을 남긴다.
  const dismissLibrarySuggestion = async (conversationId, fileId) => {
    if (!conversationId || !isPersistableLibraryFileId(fileId)) {
      return { success: false, error: 'invalid-args' };
    }
    const normalizedFileId = String(fileId).trim();

    let nextIds = [normalizedFileId];
    setConversations((prev) => {
      const updated = prev.map((conv) => {
        if (conv.id !== conversationId) return conv;
        const existing = normalizeDismissedLibrarySuggestionFileIds(
          conv.dismissedLibrarySuggestionFileIds
        );
        nextIds = existing.includes(normalizedFileId)
          ? existing
          : [...existing, normalizedFileId];
        return { ...conv, dismissedLibrarySuggestionFileIds: nextIds };
      });
      if (isAnonymous || !currentUser) {
        try {
          saveAnonymousConversations(updated);
        } catch (_e) {
          // localStorage 용량 초과 등 — 화면 상태는 이미 갱신됐다.
        }
      }
      return updated;
    });

    if (isAnonymous || !currentUser) {
      return { success: true, persisted: 'local', dismissedLibrarySuggestionFileIds: nextIds };
    }

    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        dismissedLibrarySuggestionFileIds: arrayUnion(normalizedFileId),
      });
      return { success: true, persisted: 'firestore', dismissedLibrarySuggestionFileIds: nextIds };
    } catch (err) {
      console.warn('자료함 제안 숨김 저장 실패:', err?.code || err?.message || err);
      return { success: false, error: err };
    }
  };

  const setConversationPinned = async (conversationId, pinned) => {
    if (!conversationId) return { success: false, error: 'no-conversation-id' };
    const previous = conversations;
    const updated = sortConversationsPinnedFirst(
      conversations.map((conversation) => (
        conversation.id === conversationId
          ? {
              ...conversation,
              pinned: Boolean(pinned),
              pinnedAt: pinned ? new Date() : null,
            }
          : conversation
      ))
    );
    setConversations(updated);

    if (isAnonymous) {
      saveAnonymousConversations(updated);
      return { success: true };
    }
    if (!currentUser) {
      setConversations(previous);
      return { success: false, error: 'auth-required' };
    }

    const result = await _updateConversationWithRetry(
      conversationId,
      {
        pinned: Boolean(pinned),
        pinnedAt: pinned ? serverTimestamp() : null,
      },
      '대화 고정',
    );
    if (!result.success) {
      setConversations(previous);
    }
    return result;
  };

  const renameConversation = async (conversationId, nextTitle) => {
    const title = normalizeConversationTitle(nextTitle);
    if (!conversationId || !title) {
      return { success: false, error: 'invalid-title' };
    }
    const previous = conversations;
    const updated = conversations.map((conversation) => (
      conversation.id === conversationId
        ? { ...conversation, title, titleManuallyEdited: true }
        : conversation
    ));
    setConversations(updated);

    if (isAnonymous) {
      saveAnonymousConversations(updated);
      return { success: true, title };
    }
    if (!currentUser) {
      setConversations(previous);
      return { success: false, error: 'auth-required' };
    }

    const result = await _updateConversationWithRetry(
      conversationId,
      {
        title,
        titleManuallyEdited: true,
        titleUpdatedAt: serverTimestamp(),
      },
      '대화 이름',
    );
    if (!result.success) {
      setConversations(previous);
      return result;
    }
    return { success: true, title };
  };

  const value = {
    conversations,
    currentConversation,
    unreadableConversationId,
    messages,
    loading,
    error,
    clearErrorMessages,
    setCurrentConversation,
    sendMessage,
    sendMessageStream, // 🚀 새로운 스트리밍 함수 추가
    beginPendingTurn, // 📺 전송 전 준비 단계 자리표시자
    updatePendingTurnStatus,
    cancelPendingTurn,
    createNewConversation,
    deleteConversation,
    setConversationPinned,
    renameConversation,
    resolveSuggestion, // 🧠 제안 카드(기억 제안 등) 응답 기록
    resolveClarifyCard, // 🃏 되묻기 카드 응답 기록
    resolveFollowupForm, // 🧭 조건 고르기 양식 제출 기록
    dismissHandoffTarget, // 카테고리 이어가기 카드 숨김 (대화 단위)
    updateConversationCategory, // 헤더 카테고리 변경 즉시 lastCategory 저장 (P1)
    addInlineImageMessage, // P3: 대화 안 이미지 메시지
    updateConversationInlineImageSession, // P3: 인라인 이미지 세션
    dismissLibrarySuggestion, // 자료함 저장 제안 "나중에" (대화 단위)
    stopResponseGeneration,
    updateSessionMapping, // 🆕 세션 매핑 업데이트 함수 추가
    sessionMap, // 🆕 세션 ID 매핑 객체 추가
    selectedModel, // 🆕 선택된 모델 (PRO AI에서 사용)
    setSelectedModel, // 🆕 모델 설정 함수 (PRO AI에서 사용)
    isAnonymous,
    createImageConversation,
    addImageMessage,
    updateImageConversationSession,
    updateImageConversationModel,
    getImageConversationModel,
    savePptToConversation: async (conversationId, downloadUrl, filename, outline, previewUrls, originalUrls, downloadMode = null) => {
      if (!conversationId) return { success: false, error: 'no-conversation-id' };
      if (currentUser) {
        const updateData = {
          pptDownloadUrl: downloadUrl,
          pptFilename: filename,
          updatedAt: serverTimestamp(),
        };
        if (outline) updateData.pptOutline = _serializeOutline(outline);
        if (previewUrls && previewUrls.length > 0) updateData.pptPreviewUrls = previewUrls;
        if (originalUrls && originalUrls.length > 0) updateData.pptOriginalUrls = originalUrls;
        if (downloadMode) updateData.pptDownloadMode = downloadMode;
        return _updateConversationWithRetry(conversationId, updateData, 'PPT 정보');
      }
      try {
        setConversations(prev => {
          const updated = prev.map(conv =>
            conv.id === conversationId
              ? {
                  ...conv,
                  pptDownloadUrl: downloadUrl,
                  pptFilename: filename,
                  ...(outline ? { pptOutline: outline } : {}),
                  ...(previewUrls && previewUrls.length > 0 ? { pptPreviewUrls: previewUrls } : {}),
                  ...(originalUrls && originalUrls.length > 0 ? { pptOriginalUrls: originalUrls } : {}),
                  ...(downloadMode ? { pptDownloadMode: downloadMode } : {}),
                }
              : conv
          );
          saveAnonymousConversations(updated);
          return updated;
        });
        return { success: true };
      } catch (err) {
        console.error('PPT 정보 로컬 저장 실패:', err);
        return { success: false, error: err };
      }
    },
    savePptOutlineToConversation: async (conversationId, outline, options = {}) => {
      if (!conversationId) return { success: false, error: 'no-conversation-id' };
      const clearGeneratedArtifacts = Boolean(options.clearGeneratedArtifacts);
      if (currentUser) {
        const updateData = {
          pptOutline: _serializeOutline(outline),
          updatedAt: serverTimestamp(),
        };
        if (clearGeneratedArtifacts) {
          updateData.pptDownloadUrl = null;
          updateData.pptFilename = null;
          updateData.pptPreviewUrls = null;
          updateData.pptOriginalUrls = null;
          updateData.pptDownloadMode = null;
        }
        const result = await _updateConversationWithRetry(
          conversationId,
          updateData,
          'PPT 아웃라인'
        );
        if (result.success) {
          syncLibraryTextOutline({ conversationId, contentType: 'ppt', outline });
        }
        return result;
      }
      try {
        setConversations(prev => {
          const updated = prev.map(conv =>
            conv.id === conversationId
              ? {
                  ...conv,
                  pptOutline: outline,
                  ...(clearGeneratedArtifacts ? {
                    pptDownloadUrl: null,
                    pptFilename: null,
                    pptPreviewUrls: null,
                    pptOriginalUrls: null,
                    pptDownloadMode: null,
                  } : {}),
                }
              : conv
          );
          saveAnonymousConversations(updated);
          return updated;
        });
        return { success: true };
      } catch (err) {
        console.error('PPT 아웃라인 로컬 저장 실패:', err);
        return { success: false, error: err };
      }
    },
    saveWorksheetOutlineToConversation: async (conversationId, outline, options = {}) => {
      if (!conversationId) return { success: false, error: 'no-conversation-id' };
      const clearGeneratedArtifacts = Boolean(options.clearGeneratedArtifacts);
      if (currentUser) {
        const updateData = {
          worksheetOutline: _serializeOutline(outline),
          updatedAt: serverTimestamp(),
        };
        if (clearGeneratedArtifacts) {
          updateData.worksheetDownloadUrl = null;
          updateData.worksheetFilename = null;
          updateData.worksheetPreviewUrls = null;
          updateData.worksheetOriginalUrls = null;
        }
        const result = await _updateConversationWithRetry(
          conversationId,
          updateData,
          '학습지 아웃라인'
        );
        if (result.success) {
          syncLibraryTextOutline({ conversationId, contentType: 'worksheet', outline });
        }
        return result;
      }
      try {
        setConversations(prev => {
          const updated = prev.map(conv =>
            conv.id === conversationId
              ? {
                  ...conv,
                  worksheetOutline: outline,
                  ...(clearGeneratedArtifacts ? {
                    worksheetDownloadUrl: null,
                    worksheetFilename: null,
                    worksheetPreviewUrls: null,
                    worksheetOriginalUrls: null,
                  } : {}),
                }
              : conv
          );
          saveAnonymousConversations(updated);
          return updated;
        });
        return { success: true };
      } catch (err) {
        console.error('학습지 아웃라인 로컬 저장 실패:', err);
        return { success: false, error: err };
      }
    },
    saveWorksheetToConversation: async (conversationId, downloadUrl, filename, previewUrls, originalUrls, outline) => {
      if (!conversationId) return { success: false, error: 'no-conversation-id' };
      if (currentUser) {
        const updateData = {
          worksheetDownloadUrl: downloadUrl,
          worksheetFilename: filename,
          updatedAt: serverTimestamp(),
        };
        if (outline) updateData.worksheetOutline = _serializeOutline(outline);
        if (previewUrls && previewUrls.length > 0) updateData.worksheetPreviewUrls = previewUrls;
        if (originalUrls && originalUrls.length > 0) updateData.worksheetOriginalUrls = originalUrls;
        return _updateConversationWithRetry(conversationId, updateData, '학습지 정보');
      }
      try {
        setConversations(prev => {
          const updated = prev.map(conv =>
            conv.id === conversationId
              ? {
                  ...conv,
                  worksheetDownloadUrl: downloadUrl,
                  worksheetFilename: filename,
                  ...(outline ? { worksheetOutline: outline } : {}),
                  ...(originalUrls && originalUrls.length > 0 ? { worksheetOriginalUrls: originalUrls } : {}),
                }
              : conv
          );
          saveAnonymousConversations(updated);
          return updated;
        });
        return { success: true };
      } catch (err) {
        console.error('학습지 정보 로컬 저장 실패:', err);
        return { success: false, error: err };
      }
    },

    // 편집 상태 영속화 — 자유 캔버스에서 고친 학습지(textWorksheet)를 대화 문서에 그대로 보관한다.
    // canvasOutline이 null이면 캔버스 필드를 비운다(사용자가 편집본을 버린 경우).
    // 반환: { success, error?: 'too-large' | Error, updatedAt }
    //  - too-large: Firestore 1MB 한도 위험 → 호출부가 로컬 미러(IndexedDB)에만 두고 안내한다.
    saveWorksheetCanvasToConversation: async (conversationId, canvasOutline, options = {}) => {
      if (!conversationId) return { success: false, error: 'no-conversation-id' };
      const updatedAt = options.updatedAt || new Date().toISOString();
      const canvasVersionId = options.canvasVersionId || null;
      const clearing = canvasOutline == null;
      let serialized = null;
      if (!clearing) {
        serialized = _serializeOutline(canvasOutline);
        const size = typeof serialized === 'string' ? serialized.length : 0;
        if (size > WORKSHEET_CANVAS_MAX_BYTES) {
          console.warn(`학습지 캔버스가 너무 커서 서버 저장을 건너뜁니다 (${size} bytes)`);
          return { success: false, error: 'too-large', size, updatedAt };
        }
      }
      if (currentUser) {
        const updateData = {
          worksheetCanvas: clearing ? null : serialized,
          worksheetCanvasVersionId: clearing ? null : canvasVersionId,
          worksheetCanvasUpdatedAt: clearing ? null : updatedAt,
          updatedAt: serverTimestamp(),
        };
        const result = await _updateConversationWithRetry(conversationId, updateData, '학습지 캔버스');
        if (result.success && !clearing && canvasOutline) {
          syncLibraryTextOutline({ conversationId, contentType: 'worksheet', outline: canvasOutline });
        }
        return { ...result, updatedAt };
      }
      try {
        setConversations(prev => {
          const updated = prev.map(conv =>
            conv.id === conversationId
              ? {
                  ...conv,
                  worksheetCanvas: clearing ? null : canvasOutline,
                  worksheetCanvasVersionId: clearing ? null : canvasVersionId,
                  worksheetCanvasUpdatedAt: clearing ? null : updatedAt,
                }
              : conv
          );
          saveAnonymousConversations(updated);
          return updated;
        });
        return { success: true, updatedAt };
      } catch (err) {
        console.error('학습지 캔버스 로컬 저장 실패:', err);
        return { success: false, error: err, updatedAt };
      }
    },

    // 산출물 레코드(utils/artifactRecords) — 대화 문서 worksheetArtifacts[]/pptArtifacts[]에 최신 우선·최대 20개.
    // messageId를 주면 그 AI 메시지의 artifacts[]에도 같은 레코드를 붙인다(채팅 말풍선 안 결과 카드).
    // 레거시 필드(worksheetDownloadUrl 등)는 기존 save*ToConversation이 계속 기록한다.
    saveArtifactRecordToConversation: async (conversationId, category, record, options = {}) => {
      if (!conversationId || !record || !record.id) return { success: false, error: 'invalid-args' };
      const field = category === 'ppt_maker' ? 'pptArtifacts' : 'worksheetArtifacts';
      const current = conversations.find((conv) => conv.id === conversationId);
      const nextList = upsertArtifactRecord(current?.[field], record, { limit: ARTIFACT_RECORDS_LIMIT });
      const messageId = options.messageId || null;

      const withRecord = (list) => [
        ...(Array.isArray(list) ? list : []).filter((item) => item?.id !== record.id),
        record,
      ];
      const attachToMessage = async () => {
        if (!messageId) return;
        const target = messages.find((msg) => msg.id === messageId);
        const nextArtifacts = withRecord(target?.artifacts);
        if (currentUser) {
          const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
          try {
            await runTransaction(db, async (tx) => {
              const snap = await tx.get(messageRef);
              tx.update(messageRef, { artifacts: withRecord(snap.exists() ? snap.data()?.artifacts : target?.artifacts) });
            });
          } catch (err) {
            // 메시지 첨부는 보조 저장소 — 실패해도 대화 문서 레코드가 진실이다.
            console.warn('메시지 산출물 첨부 실패:', err?.code || err?.message || err);
          }
          return;
        }
        setMessages((prev) => {
          const updated = prev.map((msg) => (msg.id === messageId ? { ...msg, artifacts: nextArtifacts } : msg));
          try { saveAnonymousMessages(conversationId, updated); } catch (_e) { /* noop */ }
          return updated;
        });
      };

      if (currentUser) {
        // 한글 자동 생성처럼 수십 초 걸리는 저장이 그사이 저장된 다른 레코드를 지우지 않도록
        // 화면 상태가 아니라 서버의 최신 목록에 합쳐 쓴다.
        const convRef = doc(db, 'conversations', conversationId);
        try {
          const records = await runTransaction(db, async (tx) => {
            const snap = await tx.get(convRef);
            const serverList = snap.exists() ? snap.data()?.[field] : current?.[field];
            const merged = upsertArtifactRecord(serverList, record, { limit: ARTIFACT_RECORDS_LIMIT });
            tx.update(convRef, { [field]: merged, updatedAt: serverTimestamp() });
            return merged;
          });
          await attachToMessage();
          return { success: true, records };
        } catch (err) {
          // 오프라인 등 트랜잭션을 못 쓰는 경우에만 예전 방식(대기열에 쌓이는 updateDoc)으로 저장한다.
          console.warn('산출물 레코드 트랜잭션 실패, 일반 저장으로 재시도:', err?.code || err?.message || err);
        }
        const result = await _updateConversationWithRetry(
          conversationId,
          { [field]: nextList, updatedAt: serverTimestamp() },
          '산출물 레코드'
        );
        if (result.success) await attachToMessage();
        return { ...result, records: nextList };
      }
      try {
        setConversations(prev => {
          const updated = prev.map(conv => (
            conv.id === conversationId ? { ...conv, [field]: nextList } : conv
          ));
          saveAnonymousConversations(updated);
          return updated;
        });
        await attachToMessage();
        return { success: true, records: nextList };
      } catch (err) {
        console.error('산출물 레코드 로컬 저장 실패:', err);
        return { success: false, error: err };
      }
    },

    // 🆕 outline 버전 히스토리 — 채팅 인라인 보존을 위한 영속 저장소
    // subcollection: conversations/{convId}/outlineVersions/{versionId}
    // 비로그인은 conv.outlineVersions[] 로컬에만 보관 (saveAnonymousConversations 통해 localStorage).
    //
    // meta:
    //  - triggerKind / triggerMessageId / parentVersionId / changesSummary (기본)
    //  - imageData: { previewUrls, originalUrls, downloadUrl, filename, fileId } | null
    //    이미지 PPT/학습지가 생성되어 산출물이 있는 버전이면 함께 저장.
    //    이 버전으로 "되돌리기" 시 이미지/다운로드까지 정확히 복원되도록 보존.
    //  - liveAnchor: { messageId, at } | undefined — 채팅이 만든/고친 버전이면 결과물 자리를 같은 쓰기로 옮긴다.
    //    버튼(다시 디자인·되돌리기 등)으로 만든 버전은 넘기지 않아 자리가 그대로다.
    pushOutlineVersion: async (conversationId, category, outline, meta = {}) => {
      if (!conversationId || !outline) return { success: false, error: 'invalid-args' };
      const headField = category === 'ppt_maker' ? 'pptActiveVersionId' : 'worksheetActiveVersionId';
      const cacheField = category === 'ppt_maker' ? 'pptOutline' : 'worksheetOutline';
      const anchorField = category === 'ppt_maker' ? 'pptLiveAnchor' : 'worksheetLiveAnchor';
      const liveAnchor = normalizeLiveAnchor(meta.liveAnchor);

      if (currentUser) {
        try {
          // addDoc 은 '없을 때만 만든다' 조건이라 첫 쓰기 확인을 못 받은 SDK 재시도가 already-exists 로 실패하고,
          // 그러면 아래 head(활성 버전·결과물 자리) 갱신까지 건너뛴다. id 를 먼저 정해 setDoc 으로 쓰면 재시도는 같은 문서 덮어쓰기다.
          const docRef = doc(collection(db, 'conversations', conversationId, 'outlineVersions'));
          const serialized = _serializeOutline(outline);
          const versionDoc = {
            category,
            outline: serialized,
            triggerKind: meta.triggerKind || 'manual_regen',
            triggerMessageId: meta.triggerMessageId || null,
            parentVersionId: meta.parentVersionId || null,
            changesSummary: meta.changesSummary || '',
            // 🆕 이미지/다운로드 산출물(있는 경우)
            imageData: meta.imageData || null,
            restoredFromVersionId: meta.restoredFromVersionId || null,
            freeDesign: meta.freeDesign || null,
            createdAt: serverTimestamp(),
          };
          const head = {
            [headField]: docRef.id,
            [cacheField]: serialized,
            ...(liveAnchor ? { [anchorField]: liveAnchor } : {}),
            updatedAt: serverTimestamp(),
          };
          // 버전 문서와 활성 버전(head)을 한 번에 쓴다 — head 만 실패하면 새로고침 때 이전 버전으로 돌아간다.
          let lastErr = null;
          for (let attempt = 1; attempt <= 2; attempt++) {
            try {
              const batch = writeBatch(db);
              batch.set(docRef, versionDoc);
              batch.update(doc(db, 'conversations', conversationId), head);
              await batch.commit();
              return { success: true, versionId: docRef.id };
            } catch (err) {
              lastErr = err;
              if (attempt < 2) await new Promise((r) => setTimeout(r, 400));
            }
          }
          throw lastErr;
        } catch (err) {
          console.error('outline 버전 push 실패:', err);
          return { success: false, error: err };
        }
      }

      try {
        const versionId = `v_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const versionRecord = {
          versionId,
          category,
          outline,
          triggerKind: meta.triggerKind || 'manual_regen',
          triggerMessageId: meta.triggerMessageId || null,
          parentVersionId: meta.parentVersionId || null,
          changesSummary: meta.changesSummary || '',
          imageData: meta.imageData || null,
          restoredFromVersionId: meta.restoredFromVersionId || null,
          freeDesign: meta.freeDesign || null,
          createdAt: new Date().toISOString(),
        };
        setConversations(prev => {
          const updated = prev.map(conv => {
            if (conv.id !== conversationId) return conv;
            const versions = Array.isArray(conv.outlineVersions) ? conv.outlineVersions : [];
            return {
              ...conv,
              outlineVersions: [...versions, versionRecord],
              [headField]: versionId,
              [cacheField]: outline,
              ...(liveAnchor ? { [anchorField]: liveAnchor } : {}),
            };
          });
          saveAnonymousConversations(updated);
          return updated;
        });
        return { success: true, versionId };
      } catch (err) {
        console.error('outline 버전 로컬 push 실패:', err);
        return { success: false, error: err };
      }
    },

    // 🆕 활성 outline 버전에 imageData만 patch (이미지 생성/inpaint 완료 후 호출)
    // 새 버전을 만들지 않고 현재 버전에 산출물만 부착 → 이전 outline 버전의 이미지도 보존됨.
    updateOutlineVersionImageData: async (conversationId, versionId, imageData) => {
      if (!conversationId || !versionId || !imageData) return { success: false, error: 'invalid-args' };

      // 'legacy_v1' 가상 버전은 subcollection 문서가 없음 → 새 버전으로 승격 필요.
      if (versionId === 'legacy_v1') {
        return { success: false, error: 'legacy-version' };
      }

      if (currentUser) {
        try {
          await updateDoc(
            doc(db, 'conversations', conversationId, 'outlineVersions', versionId),
            { imageData }
          );
          return { success: true };
        } catch (err) {
          console.error('outline 버전 imageData patch 실패:', err);
          return { success: false, error: err };
        }
      }

      try {
        setConversations(prev => {
          const updated = prev.map(conv => {
            if (conv.id !== conversationId) return conv;
            const versions = Array.isArray(conv.outlineVersions) ? conv.outlineVersions : [];
            return {
              ...conv,
              outlineVersions: versions.map(v => v.versionId === versionId ? { ...v, imageData } : v),
            };
          });
          saveAnonymousConversations(updated);
          return updated;
        });
        return { success: true };
      } catch (err) {
        return { success: false, error: err };
      }
    },

    // 텍스트형 덱이 이 버전의 개요와 맞으면 덱 참조를 버전에 붙인다(null 이면 지운다).
    // 이 버전으로 되돌리면 다시 만들지 않고 그때 덱을 바로 불러온다.
    updateOutlineVersionFreeDesign: async (conversationId, versionId, freeDesign) => {
      if (!conversationId || !versionId || versionId === 'legacy_v1') {
        return { success: false, error: 'invalid-args' };
      }
      if (freeDesign !== null && !freeDesign?.deck_id) return { success: false, error: 'invalid-args' };
      if (currentUser) {
        try {
          await updateDoc(doc(db, 'conversations', conversationId, 'outlineVersions', versionId), { freeDesign });
          return { success: true };
        } catch (err) {
          console.warn('outline 버전 덱 참조 저장 실패 (무시):', err?.code || err?.message || err);
          return { success: false, error: err };
        }
      }
      setConversations((prev) => {
        const updated = prev.map((conv) => (conv.id !== conversationId ? conv : {
          ...conv,
          outlineVersions: (Array.isArray(conv.outlineVersions) ? conv.outlineVersions : [])
            .map((v) => (v.versionId === versionId ? { ...v, freeDesign } : v)),
        }));
        saveAnonymousConversations(updated);
        return updated;
      });
      return { success: true };
    },

    loadOutlineVersions: async (conversationId, category) => {
      if (!conversationId) return { success: true, versions: [] };

      if (currentUser) {
        try {
          const versionsRef = collection(db, 'conversations', conversationId, 'outlineVersions');
          const q = query(
            versionsRef,
            where('category', '==', category),
            orderBy('createdAt', 'asc'),
          );
          const snap = await getDocs(q);
          const versions = snap.docs.map((d) => {
            const data = d.data();
            return {
              versionId: d.id,
              category: data.category,
              outline: _deserializeOutline(data.outline),
              triggerKind: data.triggerKind || 'manual_regen',
              triggerMessageId: data.triggerMessageId || null,
              parentVersionId: data.parentVersionId || null,
              changesSummary: data.changesSummary || '',
              imageData: data.imageData || null,
              restoredFromVersionId: data.restoredFromVersionId || null,
              freeDesign: data.freeDesign || null,
              createdAt: data.createdAt?.toDate?.()?.toISOString() || null,
            };
          });
          return { success: true, versions };
        } catch (err) {
          console.error('outline 버전 로드 실패:', err);
          return { success: false, error: err, versions: [] };
        }
      }

      const conv = conversations.find((c) => c.id === conversationId);
      const all = Array.isArray(conv?.outlineVersions) ? conv.outlineVersions : [];
      return { success: true, versions: all.filter((v) => v.category === category) };
    },

    setActiveOutlineVersion: async (conversationId, category, versionId, outline) => {
      if (!conversationId || !versionId) return { success: false, error: 'invalid-args' };
      const headField = category === 'ppt_maker' ? 'pptActiveVersionId' : 'worksheetActiveVersionId';
      const cacheField = category === 'ppt_maker' ? 'pptOutline' : 'worksheetOutline';

      if (currentUser) {
        return _updateConversationWithRetry(conversationId, {
          [headField]: versionId,
          ...(outline ? { [cacheField]: _serializeOutline(outline) } : {}),
          updatedAt: serverTimestamp(),
        }, '활성 버전');
      }

      try {
        setConversations(prev => {
          const updated = prev.map(conv => conv.id === conversationId
            ? { ...conv, [headField]: versionId, ...(outline ? { [cacheField]: outline } : {}) }
            : conv);
          saveAnonymousConversations(updated);
          return updated;
        });
        return { success: true };
      } catch (err) {
        return { success: false, error: err };
      }
    },
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

// 챗 컨텍스트 사용을 위한 커스텀 훅
export const useChat = () => {
  return useContext(ChatContext);
};

export default ChatContext; 